import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  WrenchScrewdriverIcon,
  ArrowDownTrayIcon,
  PhoneArrowUpRightIcon,
  XMarkIcon,
  ChevronDownIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import {
  listPostventaVehicles,
  getPostventaVehicle,
  exportPostventaVehicles,
  startPostventaOutreach,
  getPostventaInteractions,
  getPostventaLocations,
  getPostventaFreshness,
  getPostventaAsesores,
  getPostventaEjecutivosCitas,
  assignPostventaEjecutivo,
  upsertPostventaVehicle,
  relaunchPostventaFollowup,
} from '../../services/postventaApi';
import { listPhoneNumbers } from '../../services/campaignApi';
import { api } from '../../services/api';
import WhatsAppThreadPanel from '../whatsapp/WhatsAppThreadPanel';

// ---------------------------------------------------------------------------
// Interacciones: two stacked collapsibles inside the vehicle drawer.
//  - Llamadas: expandable rows (transcript bubbles + outcome badge). Renders an
//    <audio> player ONLY when a row carries recording_url (schema has none
//    today, so this stays hidden — we do NOT promise audio playback).
//  - WhatsApp: drops in the existing <WhatsAppThreadPanel> keyed by the vehicle
//    PHONE (the panel treats 404 as an empty state on its own). Rendered
//    INTERACTIVE (no readOnly) so a postventa advisor can manually reply and
//    toggle the per-thread AI on/off exactly like the campaign thread — scoped
//    to department="postventa" so it never crosses into the ventas partition
//    (POSTVENTA-WA-MANUAL-TAKEOVER-aug28).
// Kept inline (single file) on purpose — minimal-surgical lens, no util refactor.
// ---------------------------------------------------------------------------

function fmtDuration(sec) {
  if (sec == null || Number.isNaN(sec)) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  // Append the unit so "2:00" isn't ambiguous (Roger #1209 fu: is it min:sec
  // or hr:min?). mm:ss min reads unambiguously as minutes:seconds.
  return `${m}:${String(s).padStart(2, '0')} min`;
}

// Call summaries arrive as model markdown ("- **Llamada**: …"); drop the bold
// markers and keep bullets readable (same rule as Llamadas pendientes).
export function plainSummary(value) {
  return String(value == null ? '' : value).replace(/\*\*/g, '').replace(/^\s*[-*]\s+/gm, '– ');
}

// Raw call/history channel enums (CALL / WHATSAPP / SMS ...) -> es-MX label.
// Falls back to Title-cased raw value for any unmapped channel.
function channelLabelEs(channel) {
  if (channel == null || channel === '') return '';
  const key = String(channel).trim().toLowerCase();
  const map = {
    call: 'Llamada',
    voice: 'Llamada',
    phone: 'Llamada',
    whatsapp: 'WhatsApp',
    wa: 'WhatsApp',
    sms: 'SMS',
    email: 'Correo',
  };
  if (map[key]) return map[key];
  const raw = String(channel).trim();
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

// Normalize a call_outcome that may be a bare string OR an object shaped like
// { outcome, customer_name, ... } (report_call_outcome tool payload). Rendering
// the object directly produced the literal "[object Object]" chip (Roger's bug).
// Return the raw outcome key string; humanization happens via outcomeLabel(t).
function outcomeKey(outcome) {
  if (outcome == null) return '';
  if (typeof outcome === 'string') return outcome;
  if (typeof outcome === 'object') {
    const k = outcome.outcome ?? outcome.result ?? outcome.label ?? '';
    return typeof k === 'string' ? k : '';
  }
  return String(outcome);
}

// Postventa is an ES-only operator page: every heading, column, step and badge
// is a Spanish literal, and only a handful of fragments go through t(). Under EN
// those fragments ("Export", "Refresh", "No contact", "Contact overdue") were
// the ONLY English words on the page (Oct-10 Amplify audit, pages/07-postventa).
// Until the page is fully localized, pin its i18n'd fragments to Spanish so the
// page reads in one language. Keys stay i18n keys (es.json), so a future full
// localization only needs to drop this pin.
const POSTVENTA_LANG = 'es';
function usePostventaT() {
  const { t, i18n } = useTranslation();
  return typeof i18n?.getFixedT === 'function' ? i18n.getFixedT(POSTVENTA_LANG) : t;
}

function OutcomeBadge({ outcome }) {
  const t = usePostventaT();
  const key = outcomeKey(outcome);
  if (!key) return null;
  return (
    <span className="ml-2 inline-block rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700">
      {outcomeLabel(t, key)}
    </span>
  );
}

// beast-...-asesor-handoff: surface 'Asesor solicitado' when a customer
// explicitly requested a human handoff (backend sets vehicle.handoff_requested
// on the WA thread / call record). Defensive: renders nothing unless truthy, so
// legacy rows without the field are unaffected.
function HandoffBadge({ requested }) {
  if (!requested) return null;
  return (
    <span className="ml-2 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
      Asesor solicitado
    </span>
  );
}

function CallRow({ call }) {
  const [open, setOpen] = useState(false);
  const transcript = Array.isArray(call.transcript) ? call.transcript : [];
  return (
    <li className="rounded-md border border-gray-200">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span className="flex min-w-0 items-center gap-2">
          <PhoneIcon className="h-4 w-4 shrink-0 text-gray-400" />
          <span className="truncate text-gray-800">
            {call.start_time
              ? new Date(call.start_time).toLocaleString('es-MX', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : call.call_sid || 'Llamada'}
          </span>
          <OutcomeBadge outcome={call.call_outcome} />
        </span>
        <span className="flex shrink-0 items-center gap-2 text-xs text-gray-400">
          {fmtDuration(call.duration)}
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-gray-100 px-3 py-2 text-sm">
          {call.summary && <p className="whitespace-pre-wrap text-gray-600">{plainSummary(call.summary)}</p>}
          {call.recording_url && (
            <audio controls src={call.recording_url} className="w-full" />
          )}
          {transcript.length > 0 ? (
            <div className="space-y-1">
              {transcript.map((turn, i) => {
                const role = (turn.role || turn.speaker || '').toLowerCase();
                const isUser =
                  role === 'user' || role === 'customer' || role === 'cliente';
                const text = turn.text || turn.content || turn.message || '';
                if (!text) return null;
                return (
                  <div
                    key={i}
                    className={`max-w-[85%] rounded-lg px-3 py-1.5 ${
                      isUser
                        ? 'bg-gray-100 text-gray-800'
                        : 'ml-auto bg-teal-600 text-white'
                    }`}
                  >
                    {text}
                  </div>
                );
              })}
            </div>
          ) : (
            !call.summary && <p className="text-gray-400">Sin transcripción.</p>
          )}
        </div>
      )}
    </li>
  );
}

function Collapsible({ icon, title, count, defaultOpen, children }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="rounded-lg border border-gray-200">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-3 py-2 text-left"
      >
        <span className="flex items-center gap-2 font-medium text-gray-700">
          {icon}
          {title}
          {count != null && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
              {count}
            </span>
          )}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <div className="border-t border-gray-100 p-3">{children}</div>}
    </div>
  );
}

// Show only the newest entry by default; keep the rest behind a "Ver más"
// toggle that expands downward (Roger's ask for both Historial de contacto and
// Llamadas — do not dump the whole list on open). `items` MUST already be
// ordered newest-first. `renderItem(item, index)` renders one row.
function NewestFirstList({ items, renderItem, moreLabel, lessLabel }) {
  const [expanded, setExpanded] = useState(false);
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return null;
  const rest = list.length - 1;
  const visible = expanded ? list : list.slice(0, 1);
  return (
    <>
      <ul className="space-y-2">{visible.map((it, i) => renderItem(it, i))}</ul>
      {rest > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-2 flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-800"
        >
          <ChevronDownIcon
            className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
          {expanded
            ? lessLabel || 'Ver menos'
            : `${moreLabel || 'Ver más'} (${rest})`}
        </button>
      )}
    </>
  );
}

function InteraccionesSection({ vehicle }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    getPostventaInteractions(vehicle.vin)
      .then((res) => {
        if (alive) setData(res?.data || { calls: [], whatsapp: {} });
      })
      .catch((e) => {
        if (alive)
          setError(e?.response?.data?.detail || 'No se pudieron cargar las interacciones. Intenta de nuevo.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [vehicle.vin]);

  const calls = data?.calls || [];
  const phone = data?.whatsapp?.phone || vehicle.telefono || '';

  const handoffRequested = !!(data?.handoff_requested);

  return (
    <div className="space-y-3 pt-2">
      <h3 className="font-medium text-gray-700">
        Interacciones
        <HandoffBadge requested={handoffRequested} />
      </h3>
      {loading ? (
        <p className="text-gray-400">Cargando…</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : (
        <>
          <Collapsible
            icon={<PhoneIcon className="h-4 w-4 text-gray-400" />}
            title="Llamadas"
            count={calls.length}
            defaultOpen
          >
            {calls.length === 0 ? (
              <p className="text-gray-400">
                Sin llamadas registradas para este vehículo.
              </p>
            ) : (
              <NewestFirstList
                items={calls}
                renderItem={(c, i) => (
                  <CallRow key={c.call_sid || c.start_time || i} call={c} />
                )}
                moreLabel="Ver llamadas anteriores"
                lessLabel="Ver menos"
              />
            )}
          </Collapsible>
          <Collapsible
            icon={<ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-400" />}
            title="WhatsApp"
          >
            {phone ? (
              <div className="flex h-96 flex-col overflow-hidden rounded-md border border-gray-100">
                <WhatsAppThreadPanel leadPhone={phone} department="postventa" />
              </div>
            ) : (
              <p className="text-gray-400">
                Este vehículo no tiene teléfono registrado.
              </p>
            )}
          </Collapsible>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Postventa page: VIN table with search / filter / sort + a detail DRAWER
// (NOT a route — dodges the canAccessRoute param gotcha). Export downloads the
// backend-generated .xlsx blob. Outreach itself reuses the campaign engine, so
// this page is read/track focused.
// ---------------------------------------------------------------------------

// P1 postventa cleanup (Roger oct22 12:41: "clean the part of postventas to be
// understandable and have all the real information we need"). The row reads
// like the asesor's own cartera sheet — one unit, one sucursal, the two
// service dates, the seguimiento — instead of 14 columns of which four were
// empty on >99% of the 11,119 real rows (Asesor B.PRO 12, Ejecutivo 4, Lugar
// de compra 44, Lugar de servicio 48) while the populated `sucursal` (11,102)
// was never shown. Folded, not lost: teléfono sits under the name, VIN (serie)
// under the unit, km under the last-service date, intentos under the last
// contact; Asesor B.PRO + Lugar de compra/servicio stay in the drawer.
const COLUMNS = [
  { key: 'select', label: '' },
  { key: 'nombre', label: 'Cliente' },
  { key: 'modelo', label: 'Unidad' },
  { key: 'sucursal', label: 'Sucursal' },
  { key: 'ultimo_servicio', label: 'Último servicio' },
  { key: 'overdue', label: 'Próximo servicio' },
  // ticket v2 (Sergio oct20): read-only derived "Estado de seguimiento"
  // (vehicle.followup_state, null when the service is not overdue).
  { key: 'seguimiento', label: 'Seguimiento' },
  { key: 'ultimo_contacto', label: 'Último contacto' },
  { key: 'ejecutivo', label: 'Ejecutivo de citas' },
];

// Sortable epoch-ms for a possibly mixed-format service date (ISO / dd/mm/yyyy
// / epoch); -Infinity when unknown so blanks sort last on asc.
function serviceDateKey(val) {
  if (val == null || val === '') return -Infinity;
  const s = String(val).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 12).getTime();
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slash) {
    const a = Number(slash[1]);
    const b = Number(slash[2]);
    const [day, month] = b > 12 && a <= 12 ? [b, a] : [a, b];
    return new Date(Number(slash[3]), month - 1, day, 12).getTime();
  }
  const ms = parseTs(s);
  return ms == null ? -Infinity : ms;
}

// Rendered-row window (iPad "se traba", Hugo Oct 6). The cartera is ~11k
// vehicles (7.7 MB JSON on TESTING) and the table used to mount EVERY filtered
// row at once (~25 DOM nodes/row -> ~280k nodes), so each keystroke, checkbox or
// sort re-rendered 11k rows and froze Safari on iPad. Search/filter/sort/stats/
// "select all" still run over the FULL list; only the <tbody> is windowed to one
// page at a time. Page size options kept small on purpose.
export const POSTVENTA_PAGE_SIZE_DEFAULT = 100;
export const POSTVENTA_PAGE_SIZE_OPTIONS = [50, 100, 250];
// MASS-LAUNCH CAP (oct23, td473 verify P1 before the Grupo Cruces training):
// mirrors web_api_server.POSTVENTA_OUTREACH_MAX_ITEMS. Above this many
// selected clients the selection bar shows an inline warning and the Paso 3
// confirm requires an explicit acknowledgement that states the count. The
// backend refuses > cap with 400 max_items_exceeded unless an ADMIN sends
// allow_mass=true (which this page only sends after that acknowledgement).
export const POSTVENTA_OUTREACH_MAX_ITEMS = 50;

// "Mis clientes" chip tooltip when the logged-in user is in neither the B.PRO
// asesor mapping nor the ejecutivos-de-citas catalog. Points at the REAL admin
// path (Equipo → Ejecutivos de citas); the old copy said "configúralo en
// Equipo" which has no asesor-mapping UI at all (td473 verify P1, oct23).
export const MIS_CLIENTES_NOT_IN_CATALOG_TOOLTIP =
  'Pídele al admin que te agregue en Equipo → Ejecutivos de citas';

// Pure helper (unit-tested): clamp a page index to the available range and
// return the slice of rows to render plus the 1-based "a–b de N" labels.
export function pageWindow(rows, page, pageSize) {
  const total = Array.isArray(rows) ? rows.length : 0;
  const size = Math.max(1, Number(pageSize) || POSTVENTA_PAGE_SIZE_DEFAULT);
  const pageCount = Math.max(1, Math.ceil(total / size));
  const safePage = Math.min(Math.max(0, Number(page) || 0), pageCount - 1);
  const start = safePage * size;
  const end = Math.min(total, start + size);
  return {
    items: total ? rows.slice(start, end) : [],
    page: safePage,
    pageCount,
    total,
    from: total ? start + 1 : 0,
    to: end,
  };
}

// The Asesor (advisor) shown on a row: prefer the human-readable cartera name
// (advisor_name / "Asesor" column), fall back to any resolved advisor_id.
// Blank stays blank ("Sin asesor") — never fabricated.
function asesorOf(v) {
  const n = String(v?.advisor_name || '').trim();
  if (n) return n;
  const id = String(v?.advisor_id || '').trim();
  return id;
}

// The Ejecutivo de citas assigned to a row (appointment executive; distinct
// from the asesor de servicio). Blank stays blank ("Sin ejecutivo").
function ejecutivoOf(v) {
  return String(v?.ejecutivo_citas || '').trim();
}

// Paso 2 scope chips: narrow the cartera by ejecutivo scope ("¿a quién?").
// Counts are computed client-side from the loaded rows so each badge matches
// the view. Roger (2026-09-04): the scope is by EJECUTIVO consistently — the
// old "Sin asesor" chips were renamed to "Sin ejecutivo" because asesor =
// B.PRO info only, never a contact scope. (The underlying rows are still keyed
// off the row's advisor for the "mine" match — see isMine — so behavior is
// unchanged; only the labels are corrected.)
// oct23 (td473 verify P1): the 'me' / 'me+none' chips are rendered ONLY when
// the logged-in user has a B.PRO asesor mapping (myAsesorName). Without one
// the ejecutivo-de-citas chip is the single "Mis clientes" (see the chips
// block in Paso 2).
const ASESOR_SCOPES = [
  { key: 'me', label: 'Mis clientes' },
  { key: 'none', label: 'Sin ejecutivo' },
  { key: 'me+none', label: 'Mis + sin ejecutivo' },
  { key: 'all', label: 'Todos' },
];

// Human-readable department label + badge tint for the Agente IA selector
// (Paso 1). Agents carry agent.department in {"postventa", "ventas_cobranza", …}
// from the /agents serializer; we surface it so the user always sees WHICH
// agent (and which department) will call/write. Unknown/blank departments fall
// back to a neutral "General" badge rather than being hidden.
const DEPARTMENT_LABELS = {
  postventa: 'Postventa',
  ventas_cobranza: 'Ventas / Cobranza',
  ventas: 'Ventas',
  cobranza: 'Cobranza',
};
function departmentLabel(dept) {
  const key = String(dept || '').trim().toLowerCase();
  return DEPARTMENT_LABELS[key] || (key ? key : 'General');
}
function departmentBadgeClass(dept) {
  const key = String(dept || '').trim().toLowerCase();
  if (key === 'postventa') return 'bg-teal-100 text-teal-800';
  if (key === 'ventas_cobranza' || key === 'ventas' || key === 'cobranza')
    return 'bg-indigo-100 text-indigo-800';
  return 'bg-gray-100 text-gray-700';
}

// Is an agent a postventa-department agent? (Roger 2026-09-04 Paso 1 clarity:
// postventa agents surface FIRST; every other department is collapsed under
// "Otros" so a ventas agent is never the obvious/first pick on a postventa page.)
function isPostventaDept(dept) {
  return String(dept || '').trim().toLowerCase() === 'postventa';
}

// Split the loaded agents into { postventa, otros } — postventa first, the rest
// (ventas/cobranza/unknown) grouped after. Order inside each group is stable
// (as returned by /agents). Falsy entries are dropped defensively.
function groupAgentsByDepartment(agents) {
  const postventa = [];
  const otros = [];
  (agents || []).filter(Boolean).forEach((a) => {
    if (isPostventaDept(a.department)) postventa.push(a);
    else otros.push(a);
  });
  return { postventa, otros };
}

// localStorage key for "remember the last Agente IA choice per user" (Roger
// 2026-09-04). Scoped by user email so switching accounts on the same browser
// never leaks one user's last pick to another. A remembered VENTAS agent is
// still NOT auto-applied on a postventa page — see the restore effect.
function lastAgentStorageKey(userEmail) {
  const who = String(userEmail || '').trim().toLowerCase() || 'anon';
  return `postventa:lastAgentId:${who}`;
}

// Group key for the Sucursal filter: the row's normalized sucursal label
// (cartera `sucursal`, then lugar_servicio, then lugar_compra — see
// rowSucursal); never fabricate — blank stays blank ("Sin sucursal").
function locationOf(v) {
  return rowSucursal(v);
}

// TZ-safe days-overdue: (today - next_service_due) in whole days, UTC-anchored so
// no off-by-one from local tz. Positive = overdue, negative = future, null = no date.
function daysOverdue(v) {
  const due = String(v?.next_service_due || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) return null;
  const dueMs = Date.parse(due + 'T00:00:00Z');
  if (Number.isNaN(dueMs)) return null;
  const now = new Date();
  const todayMs = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  );
  return Math.round((todayMs - dueMs) / 86400000);
}

// Relative Spanish badge from days-overdue.
function OverdueBadge({ vehicle }) {
  const d = daysOverdue(vehicle);
  if (d == null) {
    return <span className="text-gray-300">—</span>;
  }
  let cls;
  let text;
  if (d >= 365) {
    const yrs = Math.floor(d / 365);
    cls = 'bg-red-50 text-red-700';
    text = `Vencido hace ${yrs} año${yrs > 1 ? 's' : ''}`;
  } else if (d >= 0) {
    cls = 'bg-amber-50 text-amber-700';
    if (d >= 30) {
      const m = Math.floor(d / 30);
      text = `Vencido hace ${m} mes${m > 1 ? 'es' : ''}`;
    } else if (d === 0) {
      text = 'Vence hoy';
    } else {
      text = `Vencido hace ${d} día${d > 1 ? 's' : ''}`;
    }
  } else {
    cls = 'bg-green-50 text-green-700';
    // Normalize the due date to es-MX (falls back to the raw ISO slice only if
    // it isn't a recognizable date) so the list never shows mixed formats.
    text = `Próximo: ${esDateOrNull(vehicle.next_service_due) || String(vehicle.next_service_due).slice(0, 10)}`;
  }
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {text}
    </span>
  );
}

// Parse a contact-history timestamp into epoch-ms. `ts` is stored as epoch-ms
// (int) by the backend (_now_ms / seed scripts), but be defensive and also
// accept ISO strings and numeric strings so nothing ever renders a raw epoch.
function parseTs(ts) {
  if (ts == null || ts === '') return null;
  if (typeof ts === 'number' && Number.isFinite(ts)) return ts;
  const s = String(ts).trim();
  if (/^\d{10}$/.test(s)) return Number(s) * 1000; // epoch seconds
  if (/^\d{13}$/.test(s)) return Number(s); // epoch millis
  const parsed = Date.parse(s); // ISO / other
  return Number.isNaN(parsed) ? null : parsed;
}

// Sortable numeric key for a contact-history entry (epoch-ms, or -Infinity).
function tsKey(ts) {
  const ms = parseTs(ts);
  return ms == null ? -Infinity : ms;
}

// Format an epoch-ms value as a short es-MX date, e.g. "21 ene 2026".
function formatDateEs(ts) {
  const ms = parseTs(ts);
  if (ms == null) return '';
  try {
    return new Date(ms).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

// Detect a date-only / ISO / epoch value and render it as a consistent es-MX
// short date. Used to normalize raw vehicle fields (ultimo_servicio,
// next_service_due) that arrive mixed as ISO "2026-06-01", "23 ago 2026", or
// en-US "8/24/2026". Returns the es-MX formatted date, or null when the value
// is not a recognizable date (so callers can fall back to the raw string).
function esDateOrNull(val) {
  if (val == null || val === '') return null;
  // Pure numeric epochs handled by parseTs; but plain small ints (e.g. año
  // 2026, cadencia 2) must NOT be mistaken for a date, so require a
  // date-shaped string or an epoch of realistic magnitude.
  if (typeof val === 'number') {
    // Only treat large numbers (>= ~year 2001 in seconds) as epochs.
    if (val < 1e9) return null;
    return formatDateEs(val);
  }
  const s = String(val).trim();
  // Date-ONLY ISO (2026-06-01): parse as LOCAL noon so a UTC-midnight parse
  // doesn't shift it to the previous day in negative-offset TZs (America/*).
  const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const [, y, mo, d] = dateOnly;
    return formatDateEs(new Date(Number(y), Number(mo) - 1, Number(d), 12).getTime());
  }
  // ISO datetime with a time part, e.g. 2026-06-01T10:00:00Z -> real instant.
  if (/^\d{4}-\d{2}-\d{2}[T ].*/.test(s)) return formatDateEs(s);
  // Slash dates. The cartera's ultimo_servicio arrives from the B.PRO/Exata
  // export as es-MX dd/mm/yyyy ("02/09/2025" = 2 sep 2025): 6,060 of 11,119
  // Grupo Cruces rows on TESTING (oct22 audit) are shaped like that and 3,780
  // of them have a day > 12, which the previous en-US Date.parse rejected
  // (raw string leaked into the drawer) while the other ~2,300 were silently
  // read month-first ("02/09/2025" -> 9 feb 2025: wrong date shown to the
  // asesor). Resolve: day-first when the second number can be a month; an
  // unambiguous en-US value (second number > 12, e.g. 8/24/2026) still parses.
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s.*)?$/);
  if (slash) {
    const a = Number(slash[1]);
    const b = Number(slash[2]);
    const y = Number(slash[3]);
    let day;
    let month;
    if (b > 12 && a <= 12) {
      month = a; // en-US m/d/yyyy (only shape where b cannot be a month)
      day = b;
    } else {
      day = a; // es-MX dd/mm/yyyy (default: the cartera's own format)
      month = b;
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const d = new Date(y, month - 1, day, 12);
    if (d.getMonth() !== month - 1) return null; // e.g. 31/02
    return formatDateEs(d.getTime());
  }
  // epoch strings (10 or 13 digits) handled by parseTs
  if (/^\d{10}$/.test(s) || /^\d{13}$/.test(s)) return formatDateEs(s);
  return null; // not a date we normalize (e.g. "23 ago 2026" is already es-ish, or a non-date)
}

// Km último servicio from the libro-de-ventas enrichment arrives as a raw
// string ("12326", occasionally "12,326" or "12326 km"). Render it with a
// thousands separator + " km" unit; null when empty/non-numeric so the drawer
// grid falls back to its em-dash convention.
export function formatKm(val) {
  if (val == null || val === '') return null;
  const digits = String(val).replace(/[^\d]/g, '');
  if (!digits) return null;
  const n = Number(digits);
  if (!Number.isFinite(n)) return null;
  return `${String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} km`;
}

// Whole CALENDAR days between today and the given epoch-ms (positive = in the
// past), in the browser's local day. Previously a rolling 24 h window: a call
// at 20:41 yesterday rendered "21 oct 2026 · hoy" next to its own date (oct22
// audit, Hugo Villaseñor). Local midnight anchoring keeps the relative word
// consistent with the formatted date beside it.
function daysSince(ts) {
  const ms = parseTs(ts);
  if (ms == null) return null;
  const d = new Date(ms);
  const now = new Date(Date.now());
  const thenDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((today - thenDay) / 86400000);
}

// ---------------------------------------------------------------------------
// Asesor-facing row helpers (P1 postventa cleanup, oct22). Pure + exported so
// the label/field mapping is unit-tested.
// ---------------------------------------------------------------------------

// Cartera `sucursal` -> the SAME labels the Calendar sucursal chips use
// (#2214 calendarTags: "Villahermosa: Deportiva" / "Villahermosa: Tabasco
// 2000" / "Cárdenas" / "Comalcalco"). The Grupo Cruces cartera carries the
// DMS agency labels, not the B.PRO branch db — on TESTING (oct22):
// 'Chevrolet Villahermosa Deportiva' 4,293 · 'CHEVROLET CRUCES DE TABASCO
// (122143)' 2,403 (incl. a NO-BREAK SPACE variant; the dealer entity / GM
// dealer code that bpro_sucursales treats as the Tabasco 2000 site alias) ·
// 'Comalcalco' 1,699 · 'Cárdenas' 1,521 · 'GCGMVILLA' 534 · 'Tab 2000
// Villahermosa' 462 · 'GCGMCARDENAS' 67 · 'GCGMCOMALCALCO' 43 · 'Chevrolet
// Villahermosa' 36 … Unknown labels pass through untouched (never dropped);
// blank stays blank. Alias set mirrors bpro_sucursales._EXTRA_ALIASES.
export const SUCURSAL_LABEL_DEPORTIVA = 'Villahermosa: Deportiva';
export const SUCURSAL_LABEL_TABASCO_2000 = 'Villahermosa: Tabasco 2000';
export const SUCURSAL_LABEL_CARDENAS = 'Cárdenas';
export const SUCURSAL_LABEL_COMALCALCO = 'Comalcalco';
const _SUCURSAL_ALIASES = {
  // Villahermosa: Deportiva (B.PRO db GCGMVILLA)
  gcgmvilla: SUCURSAL_LABEL_DEPORTIVA,
  villahermosa: SUCURSAL_LABEL_DEPORTIVA,
  deportiva: SUCURSAL_LABEL_DEPORTIVA,
  'chevrolet deportiva': SUCURSAL_LABEL_DEPORTIVA,
  'chevrolet villahermosa': SUCURSAL_LABEL_DEPORTIVA,
  'chevrolet villahermosa deportiva': SUCURSAL_LABEL_DEPORTIVA,
  'villahermosa deportiva': SUCURSAL_LABEL_DEPORTIVA,
  'chevrolet cruces villahermosa': SUCURSAL_LABEL_DEPORTIVA,
  'villahermosa: deportiva': SUCURSAL_LABEL_DEPORTIVA,
  // Villahermosa: Tabasco 2000 (no B.PRO db yet; cartera agency labels)
  'tab 2000 villahermosa': SUCURSAL_LABEL_TABASCO_2000,
  'tab 2000': SUCURSAL_LABEL_TABASCO_2000,
  'tabasco 2000': SUCURSAL_LABEL_TABASCO_2000,
  'chevrolet tabasco 2000': SUCURSAL_LABEL_TABASCO_2000,
  'villahermosa: tabasco 2000': SUCURSAL_LABEL_TABASCO_2000,
  'chevrolet cruces de tabasco (122143)': SUCURSAL_LABEL_TABASCO_2000,
  'chevrolet cruces de tabasco': SUCURSAL_LABEL_TABASCO_2000,
  'cruces de tabasco (122143)': SUCURSAL_LABEL_TABASCO_2000,
  'cruces de tabasco': SUCURSAL_LABEL_TABASCO_2000,
  // Cárdenas (GCGMCARDENAS)
  gcgmcardenas: SUCURSAL_LABEL_CARDENAS,
  cardenas: SUCURSAL_LABEL_CARDENAS,
  'chevrolet cardenas': SUCURSAL_LABEL_CARDENAS,
  'chevrolet cruces cardenas': SUCURSAL_LABEL_CARDENAS,
  // Comalcalco (GCGMCOMALCALCO)
  gcgmcomalcalco: SUCURSAL_LABEL_COMALCALCO,
  comalcalco: SUCURSAL_LABEL_COMALCALCO,
  'chevrolet comalcalco': SUCURSAL_LABEL_COMALCALCO,
  'chevrolet cruces comalcalco': SUCURSAL_LABEL_COMALCALCO,
};
function _normSucursal(raw) {
  return String(raw == null ? '' : raw)
    .replace(/\u00a0/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}
export function sucursalLabel(raw) {
  const s = String(raw == null ? '' : raw).replace(/\u00a0/g, ' ').trim();
  if (!s) return '';
  return _SUCURSAL_ALIASES[_normSucursal(s)] || s;
}

// The sucursal shown for a cartera row: the cartera `sucursal` (11,102 of
// 11,119 rows on TESTING) first, then lugar_servicio, then lugar_compra (the
// two legacy columns only 48 / 44 rows carry). Returns the display label.
export function rowSucursal(v) {
  return sucursalLabel(v?.sucursal) || sucursalLabel(v?.lugar_servicio) || sucursalLabel(v?.lugar_compra);
}

// "Unidad" = marca modelo versión · año · transmisión, from whatever the row
// carries (version / transmision / km live on the FULL row and on the slim
// list once the backend projects them). Never fabricates: blanks are skipped,
// a brand already inside modelo is not repeated. '' when nothing is known.
export function unidadParts(v) {
  const marca = String(v?.marca || '').trim();
  const modelo = String(v?.modelo || '').trim();
  const version = String(v?.version || '').trim();
  return {
    marca: marca && !modelo.toLowerCase().includes(marca.toLowerCase()) ? marca : '',
    modelo,
    version: version && !modelo.toLowerCase().includes(version.toLowerCase()) ? version : '',
    anio: String(v?.anio || '').trim(),
    transmision: String(v?.transmision || '').trim(),
  };
}
export function unidadLabel(v) {
  const p = unidadParts(v);
  let out = [p.marca, p.modelo, p.version].filter(Boolean).join(' ');
  if (p.anio) out = out ? `${out} ${p.anio}` : p.anio;
  if (p.transmision) out = out ? `${out} · ${p.transmision}` : p.transmision;
  return out;
}

// Inline "Unidad" renderer shared by the table cell and the drawer header: each
// part in its own span (modelo bold, año/transmisión muted) so the unit reads
// as one line while the modelo stays an exact text node for row lookups.
function UnidadInline({ parts, className }) {
  const u = parts || {};
  return (
    <span className={className || 'text-gray-800'}>
      {u.marca ? <span>{u.marca}</span> : null}
      {u.marca && u.modelo ? ' ' : ''}
      {u.modelo ? <span className="font-medium">{u.modelo}</span> : null}
      {u.version ? <span> {u.version}</span> : null}
      {u.anio ? <span className="text-gray-500"> {u.anio}</span> : null}
      {u.transmision ? <span className="text-gray-500"> · {u.transmision}</span> : null}
    </span>
  );
}

// Test / QA rows mixed into the real cartera (TESTING oct22: 'TEST - Cardenas',
// 'TEST - Eli', VIN 'TESTEQP0SEP5EQNX7', rows stamped test_tag=TEST-EQUIPO-…).
// Marked with a badge and hidden behind a toggle — NEVER deleted here (data
// hygiene is a separate task). Detection is deliberately narrow so a real
// customer surname like "Testa" is not flagged: an explicit test_tag, a
// clone/test marker, a name that STARTS with TEST/PRUEBA/DEMO/QA, or a VIN
// that contains TEST.
export function isTestRow(v) {
  if (!v) return false;
  if (String(v.test_tag || '').trim()) return true;
  if (String(v.clone_source_vin || v.clone_task || '').trim()) return true;
  const nombre = String(v.nombre || '').trim();
  if (/^(test|prueba|demo|qa)\b/i.test(nombre)) return true;
  const vin = String(v.vin || '').toUpperCase();
  if (vin.includes('TEST')) return true;
  return false;
}

// localStorage key for the "Mostrar pruebas" toggle (default: hidden).
export const SHOW_TEST_ROWS_STORAGE_KEY = 'postventa:showTestRows';

// P0 PEN-primary cartera (Grupo Cruces, Fer 2026-10-23): units outside the planta
// window (> 96 months / modelo <= año-8) are stamped `fuera_de_planta: true` by the
// PEN ingest (plus `fuera_de_planta_reason`) and are NEVER contacted. The stamp
// decides — not the year — so a row the ingest has not judged is never hidden.
// Boolean-ish only: true / 'true' / 1 / 'si'; a bare 'no' / false / missing = in planta.
export function isFueraDePlanta(v) {
  if (!v) return false;
  const val = v.fuera_de_planta;
  if (val === true || val === 1) return true;
  if (typeof val === 'string') {
    return ['true', '1', 'si', 'sí', 'x', 'yes'].includes(val.trim().toLowerCase());
  }
  return false;
}

// GM "meses sin visita" bands (overlapping; the ingest stamps every band a row
// falls in on `bandas_servicio`). 12-18 = vencidos from here; 13-48 = MAIN.
export const VISITA_BANDS = [
  { key: '5-11', label: '5–11 meses' },
  { key: '12-18', label: '12–18 meses (vencidos)' },
  { key: '13-48', label: '13–48 meses (principal)' },
  { key: '13-96', label: '13–96 meses' },
];

// Membership test on the row's overlapping `bandas_servicio` list ('all' = no filter).
export function rowInVisitaBand(v, band) {
  if (!band || band === 'all') return true;
  const bands = Array.isArray(v?.bandas_servicio) ? v.bandas_servicio : [];
  return bands.map(String).includes(String(band));
}

// Human relative Spanish string, e.g. "hoy", "ayer", "hace 25 días", "hace 3 meses".
function relativeEs(ts) {
  const d = daysSince(ts);
  if (d == null) return '';
  if (d <= 0) return 'hoy';
  if (d === 1) return 'ayer';
  if (d < 30) return `hace ${d} día${d > 1 ? 's' : ''}`;
  if (d < 365) {
    const m = Math.floor(d / 30);
    return `hace ${m} mes${m > 1 ? 'es' : ''}`;
  }
  const y = Math.floor(d / 365);
  return `hace ${y} año${y > 1 ? 's' : ''}`;
}

// Postventa informativo cadence: default 2 months (~60 days) unless the vehicle
// specifies cadence_months. Returns the cadence threshold in days.
function cadenceDays(v) {
  const m = Number(v?.cadence_months);
  const months = Number.isFinite(m) && m > 0 ? m : 2;
  return months * 30;
}

// True when the last contact is older than the informativo cadence, or the
// vehicle has never been contacted. Drives the "Contacto vencido" badge.
function contactOverdue(v, last) {
  if (!last) return true; // never contacted
  const d = daysSince(last.ts);
  if (d == null) return true;
  return d > cadenceDays(v);
}

// Human label for a contact outcome. Reuses the shared callOutcomes namespace
// (t()||fallback gotcha), then falls back to the raw value for ad-hoc outcomes
// like the demo's "recordatorio futuro".
function outcomeLabel(t, outcome) {
  if (!outcome) return '';
  const key = String(outcome);
  const translated = t(`callOutcomes.${key}`, '');
  return translated || key;
}

// ---------------------------------------------------------------------------
// 3 contact profiles (propietario / mantenimiento / usuario) + fallback order.
// The backend view always exposes `contacts` (derived from the legacy columns
// for old rows), so the UI renders them uniformly.
// ---------------------------------------------------------------------------
const PROFILE_ROLE_ORDER = ['propietario', 'mantenimiento', 'usuario'];
const PROFILE_ROLE_LABELS = {
  propietario: 'Propietario',
  mantenimiento: 'Mantenimiento',
  usuario: 'Usuario',
};

// Ordered contact-profile list for a vehicle. Uses the backend-derived
// `contacts` when present; otherwise derives a single propietario profile from
// the legacy telefono/nombre so old rows still render.
function vehicleContacts(v) {
  const list = Array.isArray(v?.contacts) ? v.contacts : [];
  if (list.length) return list;
  const nombre = String(v?.nombre || '').trim();
  const telefono = String(v?.telefono || '').trim();
  if (nombre || telefono) {
    return [{ role: 'propietario', nombre, telefono, preferido: false }];
  }
  return [];
}

// "Contactado vía: mantenimiento (Juan Pérez)" fragment for a history event,
// or '' when the event carries no profile. Accepts either the precomputed
// `contacted_via` string or a `contacted_profile` object.
function contactedViaLabel(ev) {
  if (!ev) return '';
  if (ev.contacted_via) return String(ev.contacted_via);
  const cp = ev.contacted_profile;
  if (!cp || typeof cp !== 'object') return '';
  const role = String(cp.role || '').trim();
  const nombre = String(cp.nombre || '').trim();
  const roleLabel = PROFILE_ROLE_LABELS[role] ? role : (role || 'contacto');
  return nombre ? `${roleLabel} (${nombre})` : roleLabel;
}

// List-view Último contacto cell: formatted es-MX date + relative + last outcome,
// plus an amber "Contacto vencido" badge when past the 2-month cadence (or never).
function LastContactCell({ t, vehicle, last }) {
  const overdue = contactOverdue(vehicle, last);
  const badge = overdue ? (
    <span className="mt-1 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
      {t('postventa.contactOverdue', 'Contacto vencido')}
    </span>
  ) : null;
  const followup = followupStatusLine(vehicle);
  const followupBadge = followup ? (
    <span
      data-testid={`postventa-followup-${vehicle?.vin}`}
      className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${FOLLOWUP_TONE[followup.tone] || FOLLOWUP_TONE.neutral}`}
      title="Cadencia de contacto (Postventa)"
    >
      {followup.text}
    </span>
  ) : null;

  if (!last) {
    return (
      <div className="flex flex-col">
        <span className="text-gray-300">
          {t('postventa.neverContacted', 'Sin contacto')}
        </span>
        {followupBadge}
        {badge}
      </div>
    );
  }

  const date = formatDateEs(last.ts);
  const rel = relativeEs(last.ts);
  const outcome = outcomeLabel(t, outcomeKey(last.outcome));
  return (
    <div className="flex flex-col">
      <span className="text-gray-800">
        {date || '—'}
        {rel ? <span className="text-gray-400"> · {rel}</span> : null}
      </span>
      {outcome ? (
        <span className="text-xs text-gray-500">{outcome}</span>
      ) : null}
      {followupBadge}
      {badge}
    </div>
  );
}

// oct20 CADENCE CAP (Sergio, Grupo Cruces "no hostigar"): the backend mirrors
// the contact cadence state onto the vehicle row as `followup` (status /
// cycles / paused_until / opt_out / relaunched_by) and ships a Spanish
// `followup_label`. This helper renders that state line — "No contestó · N
// ciclos sin contacto" / "Pausado hasta <fecha>" / "Detenido · N ciclos sin
// contacto" / "Opt-out" / "Contactado" / "Reanudado por <quién>" — and tells
// the drawer whether "Reanudar contacto" applies (status stopped). Pure.
export const FOLLOWUP_TONE = {
  neutral: 'bg-gray-100 text-gray-600',
  warn: 'bg-amber-50 text-amber-700',
  stop: 'bg-red-50 text-red-700',
  ok: 'bg-green-50 text-green-700',
  info: 'bg-blue-50 text-blue-700',
};

const _fmtPausedUntil = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Mexico_City' });
  } catch (e) {
    return d.toLocaleDateString('es-MX');
  }
};

// ---------------------------------------------------------------------------
// ticket v2 "Estado de seguimiento" (Sergio oct20): the backend materializes
// a derived 9-state map on the vehicle row as `followup_state` (sibling of the
// cadence mirror `followup`; null when the service is not overdue). This block
// is the ONLY reader: column cell, 7 filter chips (+ a count-only "Sin
// iniciar"), secondary chips and the drawer section. The bucket map below
// MUST mirror postventa_followup_state.FILTER_BUCKETS so the page's client-
// side counts equal the API's `followup_counts` for the same rows.
// ---------------------------------------------------------------------------
export const SEGUIMIENTO_STATES = [
  'no_contactar', 'completado', 'en_espera_asesor', 'pausado', 'fallida',
  'contesto_datos_insuficientes', 'en_progreso', 'no_contesto', 'sin_iniciar',
];
export const SEGUIMIENTO_FILTERS = [
  { key: 'en_progreso', label: 'En progreso', states: ['en_progreso'] },
  { key: 'no_contesto', label: 'No contestó', states: ['no_contesto'] },
  { key: 'en_espera_asesor', label: 'En espera de asesor', states: ['en_espera_asesor'] },
  { key: 'completado', label: 'Completado', states: ['completado'] },
  { key: 'pausado', label: 'Pausado', states: ['pausado'] },
  { key: 'no_contactar', label: 'No contactar', states: ['no_contactar'] },
  { key: 'requiere_atencion', label: 'Requiere atención', states: ['fallida', 'contesto_datos_insuficientes'] },
];
const SEGUIMIENTO_STATE_LABEL = {
  no_contactar: 'No contactar',
  completado: 'Completado',
  en_espera_asesor: 'En espera de asesor',
  pausado: 'Pausado',
  fallida: 'Fallida',
  contesto_datos_insuficientes: 'Contestó · datos insuficientes',
  en_progreso: 'En progreso',
  no_contesto: 'No contestó',
  sin_iniciar: 'Sin iniciar',
};
const SEGUIMIENTO_REASON_LABEL = {
  baja_menu_3: 'baja por menú (3)',
  baja_g24_universal: 'baja universal (G24)',
  cita_voz: 'cita agendada por voz',
  cita_whatsapp: 'cita agendada por WhatsApp',
  cita_vigente: 'cita vigente',
  solicitud_abierta: 'solicitud abierta',
  menu_1_si_sin_solicitud: 'pidió asesor (menú 1)',
  pausa_cliente_15d: 'cliente pidió esperar',
  pausa_vencida: 'pausa vencida',
  fallo_tecnico: 'fallo técnico',
  numero_equivocado: 'número equivocado',
  datos_insuficientes: 'datos insuficientes',
  cadencia_activa: 'cadencia activa',
  relanzado: 'relanzado',
  pausa_ciclo: 'pausa entre ciclos',
  ciclos_agotados: 'ciclos agotados',
  menu_sin_respuesta: 'menú sin respuesta',
  sin_contacto: 'sin contacto',
  sin_evidencia: '',
  legacy_ambiguo: 'historial legado ambiguo',
};
const SEGUIMIENTO_TONE = {
  no_contactar: 'stop', completado: 'ok', en_espera_asesor: 'info', pausado: 'warn', fallida: 'stop',
  contesto_datos_insuficientes: 'warn', en_progreso: 'info', no_contesto: 'neutral', sin_iniciar: 'neutral',
};

// Map a derived state to its filter bucket key (null for sin_iniciar / unknown).
export function seguimientoBucket(state) {
  const s = String(state || '');
  const hit = SEGUIMIENTO_FILTERS.find((f) => f.states.includes(s));
  return hit ? hit.key : null;
}

// The derived map of a vehicle, or null (not overdue / not computed yet).
// Never returns a non-object so no cell can ever render "[object Object]".
export function seguimientoOf(vehicle) {
  const st = vehicle && typeof vehicle.followup_state === 'object' ? vehicle.followup_state : null;
  return st && SEGUIMIENTO_STATES.includes(String(st.state || '')) ? st : null;
}

// { text, detail, tone, chips[] } for a vehicle, or null. Pure (t optional).
export function seguimientoLine(vehicle, t) {
  const st = seguimientoOf(vehicle);
  if (!st) return null;
  const tr = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);
  const state = String(st.state);
  const reason = String(st.reason || '');
  const base = tr(`postventa.followup.states.${state}`, SEGUIMIENTO_STATE_LABEL[state] || state);
  let text = base;
  if (state === 'no_contesto' && reason === 'ciclos_agotados') {
    const n = Number(st.cycle_count) || 0;
    text = `${base} · ${n} ciclo${n === 1 ? '' : 's'} sin contacto`;
  } else {
    const fallbackDetail = SEGUIMIENTO_REASON_LABEL[reason] ?? reason;
    // An empty fallback (sin_evidencia) means "no detail": never ask i18n for it
    // (the key would leak as text).
    const detail = fallbackDetail ? tr(`postventa.followup.reasons.${reason}`, fallbackDetail) : '';
    if (detail) text = `${base} · ${detail}`;
  }
  const chips = (Array.isArray(st.chips) ? st.chips : []).map((c) => {
    const s = String(c || '');
    if (s === 'solicitud_abierta') return tr('postventa.followup.chips.openRequest', 'Solicitud abierta');
    if (s.startsWith('restriccion_g24')) {
      const scope = s.split(':')[1] || '';
      const scopeLabel = scope
        .split('+')
        .map((x) => ({ voz: 'Voz', whatsapp: 'WhatsApp', sms: 'SMS' }[x] || x))
        .join(' + ');
      return `${tr('postventa.followup.chips.g24', 'Restricción G24')}${scopeLabel ? ` · ${scopeLabel}` : ''}`;
    }
    return s;
  });
  return { text, state, reason, tone: SEGUIMIENTO_TONE[state] || 'neutral', chips, map: st };
}

// Attempts shown in the drawer: the bounded context-scoped list the backend
// stores inside followup_state (full row), else the vehicle's call events at
// or after the context anchor (slim row fallback). Newest first.
export function seguimientoAttempts(vehicle) {
  const st = seguimientoOf(vehicle);
  if (!st) return [];
  if (Array.isArray(st.attempts) && st.attempts.length) {
    return st.attempts.map((a) => ({
      ts: a.at, call_sid: a.call_sid, delivery_status: a.delivery_status, outcome: a.outcome,
      error_code: a.error_code, phone_masked: a.phone_masked,
    }));
  }
  const anchor = parseTs(st.context_started_at);
  const hist = Array.isArray(vehicle.contact_history) ? vehicle.contact_history : [];
  return hist
    .filter((ev) => ev && ev.call_sid && (anchor == null || tsKey(ev.ts) >= anchor))
    .map((ev) => ({ ts: ev.ts, call_sid: ev.call_sid, delivery_status: '', outcome: ev.outcome, error_code: '' }))
    .sort((a, b) => tsKey(b.ts) - tsKey(a.ts));
}

function SeguimientoCell({ t, vehicle }) {
  const line = seguimientoLine(vehicle, t);
  if (!line) return <span className="text-gray-300">—</span>;
  return (
    <div className="flex flex-col gap-1" data-testid={`postventa-seguimiento-${vehicle?.vin}`}>
      <span
        className={`inline-block w-fit rounded-full px-2 py-0.5 text-xs font-medium ${FOLLOWUP_TONE[line.tone] || FOLLOWUP_TONE.neutral}`}
      >
        {line.text}
      </span>
      {line.chips.length ? (
        <span className="flex flex-wrap gap-1">
          {line.chips.map((c) => (
            <span key={c} className="inline-block rounded-full border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-600">
              {c}
            </span>
          ))}
        </span>
      ) : null}
    </div>
  );
}

export function followupStatusLine(vehicle) {
  const fu = vehicle && typeof vehicle.followup === 'object' && vehicle.followup ? vehicle.followup : null;
  if (!fu) return null;
  const status = String(fu.status || '');
  const n = Number(fu.cycles) || 0;
  const ciclos = `${n} ciclo${n === 1 ? '' : 's'} sin contacto`;
  const serverLabel = String(vehicle.followup_label || '').trim();
  if (status === 'opt_out' || fu.opt_out === true) {
    return { text: serverLabel || 'Opt-out', tone: 'stop', status: 'opt_out', canRelaunch: false };
  }
  if (status === 'stopped') {
    return { text: serverLabel || `Detenido · ${ciclos}`, tone: 'stop', status, canRelaunch: true };
  }
  if (status === 'paused') {
    const until = _fmtPausedUntil(fu.paused_until);
    return { text: serverLabel || (until ? `Pausado hasta ${until}` : 'Pausado'), tone: 'warn', status, canRelaunch: false };
  }
  if (status === 'no_answer') {
    const text = serverLabel || (n > 0
      ? `No contestó · ${ciclos}`
      : `No contestó · ciclo ${Number(fu.cycle) || 1}${fu.day_index ? `, día ${fu.day_index}` : ''}`);
    return { text, tone: 'warn', status, canRelaunch: false };
  }
  if (status === 'contacted') {
    return { text: serverLabel || 'Contactado', tone: 'ok', status, canRelaunch: false };
  }
  if (status === 'relaunched') {
    const who = String(fu.relaunched_by_email || fu.relaunched_by || '').trim();
    return { text: serverLabel || (who ? `Reanudado por ${who}` : 'Reanudado'), tone: 'info', status, canRelaunch: false };
  }
  return serverLabel ? { text: serverLabel, tone: 'neutral', status, canRelaunch: false } : null;
}

// Most-recent contact_history event (by ts) for a vehicle, or null.
function lastContact(v) {
  const hist = Array.isArray(v?.contact_history) ? v.contact_history : [];
  if (!hist.length) return null;
  return hist
    .slice()
    .sort((a, b) => tsKey(a?.ts) - tsKey(b?.ts))
    .slice(-1)[0];
}

// "Último resultado" value for the drawer. The explicit last_call_outcome field
// is often empty even when a real latest outcome exists in contact_history, so
// fall back to the newest contact event's outcome. Returns an es-MX label or
// the em dash when there is genuinely nothing (Roger #1209 fu).
function latestOutcomeLabel(t, v) {
  const explicit = outcomeLabel(t, outcomeKey(v?.last_call_outcome));
  if (explicit) return explicit;
  const l = lastContact(v);
  const fromHist = l ? outcomeLabel(t, outcomeKey(l.outcome)) : '';
  return fromHist || '—';
}

// Cartera freshness badge (branch 2b). The cartera VIN/nombre/teléfono/último-
// servicio dataset comes from a HUMAN Exata/B.PRO export POSTed to
// /api/integration/postventa/import — there is no programmatic source to auto-
// sync it, so instead of faking freshness we SHOW how old the last import is.
// Green when <= threshold days, red when older (or never imported). Manual
// refresh step is documented in docs/postventa_bpro_daily_freshness.md.
// Real B.PRO sync schedule (structDatafy cron `*/20 7-19 * * 1-6`, America/
// Mexico_City => slots 07:00..19:40 every 20 min, Mon-Sat, NO Sunday). The
// server sends the authoritative label in `freshness.bpro_sync.schedule`; this
// is the fallback for older backends that only return `cartera`.
export const BPRO_SYNC_SCHEDULE_FALLBACK = '07:00–19:40 cada 20 min, lun–sáb';

export function CarteraFreshnessBadge({ freshness }) {
  const cartera = freshness?.cartera;
  if (!cartera) return null;
  const days = cartera.last_import_age_days;
  const hours = cartera.last_import_age_hours;
  const stale = !!cartera.stale;
  const thr = cartera.stale_days_threshold;
  // Schedule-aware signal (verify-bpro-sync-stale-saturday-oct10): the server
  // counts how many due sync slots landed nothing. `overdue` => the sync is
  // stalled INSIDE its own window (Sat 2026-10-10: last push 08:40, read 13:17
  // => 13 slots missed) and the badge must NOT stay green claiming "cada 20 min".
  const sync = freshness?.bpro_sync || null;
  const schedule = (sync && sync.schedule) || BPRO_SYNC_SCHEDULE_FALLBACK;
  const overdue = !!(sync && sync.overdue);
  const missed = sync && Number.isFinite(Number(sync.missed_slots)) ? Number(sync.missed_slots) : null;
  // "Sin importar" is now reserved for a genuinely EMPTY cartera. A cartera
  // with rows always shows how fresh its newest row is — derived server-side
  // from max(last_import_at, B.PRO sync run, max row updated_at) — so the old
  // Exata import that never stamped last_import_at no longer reads as empty.
  const empty =
    cartera.empty === true ||
    (cartera.empty == null && days == null && hours == null);
  let label;
  if (empty) {
    label = 'Cartera sin importar';
  } else if (hours != null && hours < 1) {
    const mins = Math.max(1, Math.round(hours * 60));
    label = `Cartera actualizada hace ${mins} min`;
  } else if (hours != null && hours < 24) {
    const h = Math.floor(hours);
    label = `Cartera actualizada hace ${h} h`;
  } else if (days != null && days < 1) {
    label = 'Cartera actualizada hoy';
  } else if (days != null) {
    const n = Math.floor(days);
    label = `Cartera actualizada hace ${n} día${n === 1 ? '' : 's'}`;
  } else {
    label = 'Cartera actualizada';
  }
  // Append the B.PRO sync context so the reader knows the cartera auto-refreshes
  // through the day (not a one-off human import) whenever it's non-empty. When
  // the sync is overdue say so explicitly instead of a green "cada 20 min".
  if (!empty && overdue) {
    label += ` · sync B.PRO atrasado${missed != null && missed > 0 ? ` (${missed} corridas sin datos)` : ''}`;
  } else if (!empty) {
    label += ` (B.PRO sync ${schedule})`;
  }
  const tone = stale ? 'red' : overdue && !empty ? 'amber' : 'emerald';
  const cls = tone === 'red'
    ? 'bg-red-50 text-red-700 ring-red-600/20'
    : tone === 'amber'
    ? 'bg-amber-50 text-amber-800 ring-amber-600/20'
    : 'bg-emerald-50 text-emerald-700 ring-emerald-600/20';
  const dot = tone === 'red' ? 'bg-red-500' : tone === 'amber' ? 'bg-amber-500' : 'bg-emerald-500';
  const lastDue = sync && sync.last_due_slot_at ? String(sync.last_due_slot_at).slice(11, 16) : null;
  const title = empty
    ? 'La cartera está vacía. Exporta de Exata/B.PRO e importa vía ' +
      '/api/integration/postventa/import (ver docs/postventa_bpro_daily_freshness.md).'
    : stale
    ? `La cartera no se ha actualizado en más de ${thr} días. ` +
      'Revisa la sincronización B.PRO o reimporta desde Exata ' +
      '(ver docs/postventa_bpro_daily_freshness.md).'
    : overdue
    ? `La sincronización B.PRO (${schedule}) no ha entregado datos en ` +
      `${missed != null ? missed : 'varias'} corridas programadas` +
      `${lastDue ? ` (última esperada ${lastDue})` : ''}. ` +
      'Revisa el scheduler de structDatafy (workflows "BPRO Cartera Sync" / "B.PRO citas sync").'
    : `Cartera dentro del umbral de frescura (${thr} días). Se actualiza con ` +
      `la sincronización B.PRO (${schedule}).`;
  return (
    <span
      data-testid="postventa-cartera-freshness"
      data-sync-overdue={overdue ? 'true' : 'false'}
      title={title}
      className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${dot}`}
      />
      {label}
    </span>
  );
}

// Canonicalize an MX phone to its 10-digit national significant number so a
// calendar-supplied phone (digits only, maybe with 52/521 country code) matches
// a cartera row's telefono regardless of country-code/mobile-1 shape. Mirrors the
// ---------------------------------------------------------------------------
// Drawer section: the 3 contact profiles in fallback order with an "Editar
// perfiles" button. Shows the last profile that answered (contactado vía) when
// known, then the ordered profile list (propietario -> mantenimiento ->
// usuario), marking the preferido and flagging profiles with no phone (not
// dialable until fixed).
// ---------------------------------------------------------------------------
function ContactProfilesSection({ vehicle, onEdit }) {
  const contacts = vehicleContacts(vehicle);
  const last = lastContact(vehicle);
  const via = contactedViaLabel(last);
  return (
    <div className="pt-2" data-testid="postventa-contact-profiles">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-medium text-gray-700">Perfiles de contacto</h3>
        <button
          type="button"
          data-testid="postventa-edit-profiles-btn"
          onClick={onEdit}
          className="rounded-md border border-indigo-200 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
        >
          Editar perfiles
        </button>
      </div>
      {via && (
        <div className="mb-2 text-xs font-medium text-indigo-600">
          {`Contactado vía: ${via}`}
        </div>
      )}
      {contacts.length === 0 ? (
        <p className="text-gray-400">Sin perfiles de contacto.</p>
      ) : (
        <ol className="space-y-1">
          {contacts.map((c, i) => {
            const roleLabel = PROFILE_ROLE_LABELS[c.role] || c.role || 'Contacto';
            const noPhone = !String(c.telefono || '').trim();
            return (
              <li
                key={`${c.role}-${c.telefono || ''}-${i}`}
                data-testid="postventa-profile-row"
                className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {`${i + 1}. ${roleLabel}`}
                    {c.preferido ? (
                      <span className="ml-2 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] text-indigo-700">
                        preferido
                      </span>
                    ) : null}
                  </span>
                  <span className="text-gray-800">{c.nombre || '—'}</span>
                </div>
                <span className={noPhone ? 'text-xs text-amber-600' : 'text-gray-700'}>
                  {noPhone ? 'sin teléfono' : String(c.telefono)}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// Edit modal: add/fix up to 3 contact profiles by hand. Saves via the VIN
// upsert (idempotent; never wipes contact history). Empty rows are dropped on
// save so an advisor can leave a profile blank.
function EditProfilesModal({ vehicle, onClose, onSaved }) {
  const seed = vehicleContacts(vehicle);
  const byRole = (role) => seed.find((c) => c.role === role) || {};
  const [rows, setRows] = useState(
    PROFILE_ROLE_ORDER.map((role) => {
      const c = byRole(role);
      return {
        role,
        nombre: c.nombre || '',
        telefono: c.telefono || '',
        preferido: !!c.preferido,
      };
    })
  );
  const [saving, setSaving] = useState(false);

  const update = (idx, patch) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const contacts = rows
        .filter((r) => String(r.nombre || '').trim() || String(r.telefono || '').trim())
        .map((r) => ({
          role: r.role,
          nombre: String(r.nombre || '').trim(),
          telefono: String(r.telefono || '').trim(),
          preferido: !!r.preferido,
        }));
      await upsertPostventaVehicle(vehicle.vin, { contacts });
      toast.success('Perfiles de contacto actualizados');
      onSaved && onSaved(contacts);
      onClose();
    } catch (e) {
      toast.error('No se pudieron guardar los perfiles');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={() => !saving && onClose()} />
      <div
        className="relative z-10 w-full max-w-lg rounded-lg bg-white shadow-xl"
        data-testid="postventa-edit-profiles-modal"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Perfiles de contacto</h2>
          <button onClick={() => !saving && onClose()} className="text-gray-400 hover:text-gray-700">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4 text-sm">
          <p className="text-xs text-gray-500">
            Orden de contacto: propietario → mantenimiento → usuario. Si el
            primero no contesta, se intenta el siguiente en el mismo paso.
          </p>
          {rows.map((r, i) => (
            <div key={r.role} className="rounded-md border border-gray-200 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {`${i + 1}. ${PROFILE_ROLE_LABELS[r.role]}`}
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input
                  data-testid={`postventa-profile-nombre-${r.role}`}
                  value={r.nombre}
                  onChange={(e) => update(i, { nombre: e.target.value })}
                  placeholder="Nombre"
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
                <input
                  data-testid={`postventa-profile-telefono-${r.role}`}
                  value={r.telefono}
                  onChange={(e) => update(i, { telefono: e.target.value })}
                  placeholder="Teléfono (E.164)"
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  data-testid={`postventa-profile-preferido-${r.role}`}
                  checked={r.preferido}
                  onChange={(e) => update(i, { preferido: e.target.checked })}
                />
                Preferido (intentar primero)
              </label>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
          <button
            onClick={() => !saving && onClose()}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="postventa-save-profiles-btn"
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// "Cita" line for the drawer from the row's `last_appointment` mirror (a dict
// written by the call/WA booking path; shape varies by writer, so read the
// usual keys defensively). '' when there is no appointment on file.
export function lastAppointmentLine(v) {
  const la = v?.last_appointment;
  if (!la) return '';
  if (typeof la === 'string') return esDateOrNull(la) || la;
  if (typeof la !== 'object') return '';
  const when = la.start_datetime || la.datetime || la.start || la.fecha || la.date || la.when || '';
  let whenText = '';
  if (when) {
    const ms = parseTs(when);
    if (ms != null && /[T ]\d{2}:\d{2}/.test(String(when))) {
      try {
        whenText = new Date(ms).toLocaleString('es-MX', {
          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
      } catch {
        whenText = esDateOrNull(when) || String(when);
      }
    } else {
      whenText = esDateOrNull(when) || String(when);
    }
  }
  const where = sucursalLabel(la.sucursal || la.branch_label || la.branch || la.location_id || la.branch_db || '');
  const what = String(la.service || la.servicio || '').trim();
  return [whenText, where, what].filter(Boolean).join(' · ');
}

// Drawer field groups (pure, unit-tested): [{ key, title, rows: [[label, value,
// opts?]] }]. Internal fields are folded into human lines: cadence_months ->
// "cada N meses" on Próximo servicio; raw cartera sucursal -> normalized label
// with the original in the tooltip; prioridad / status_cliente (B.PRO cartera
// columns the asesor already knows) only when the row carries them.
export function drawerSections(v, t) {
  const tr = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);
  const rawSucursal = String(v?.sucursal || v?.lugar_servicio || v?.lugar_compra || '').replace(/\u00a0/g, ' ').trim();
  const sucursal = rowSucursal(v);
  const km = formatKm(v?.km_ultimo_servicio);
  const ultimo = esDateOrNull(v?.ultimo_servicio) || (v?.ultimo_servicio ? String(v.ultimo_servicio) : '');
  const proximo = esDateOrNull(v?.next_service_due) || (v?.next_service_due ? String(v.next_service_due) : '');
  const cad = Number(v?.cadence_months);
  const cadText = Number.isFinite(cad) && cad > 0 ? `cada ${cad} mes${cad === 1 ? '' : 'es'}` : '';
  const d = daysOverdue(v);
  let estado = '';
  if (d != null) {
    if (d === 0) estado = 'vence hoy';
    else if (d > 0) estado = `vencido hace ${d} día${d === 1 ? '' : 's'}`;
    else estado = `en ${-d} día${d === -1 ? '' : 's'}`;
  }
  const l = lastContact(v);
  let ultimoContacto = tr('postventa.neverContacted', 'Sin contacto');
  if (l) {
    const date = formatDateEs(l.ts);
    const rel = relativeEs(l.ts);
    const outcome = outcomeLabel(t, outcomeKey(l.outcome));
    const base = [date, rel ? `· ${rel}` : ''].filter(Boolean).join(' ');
    ultimoContacto = outcome ? `${base} — ${outcome}` : base || '—';
  }
  const hist = Array.isArray(v?.contact_history) ? v.contact_history.length : 0;
  const cita = lastAppointmentLine(v);
  const cliente = [
    ['Nombre', String(v?.nombre || '').trim()],
    ['Teléfono', String(v?.telefono || '').trim() || 'Sin teléfono'],
  ];
  if (String(v?.email || '').trim()) cliente.push(['Correo', String(v.email).trim()]);
  cliente.push(['Ejecutivo de citas', ejecutivoOf(v) || 'Sin ejecutivo']);
  const unidad = [
    ['Unidad', unidadLabel(v) || 'Sin datos de la unidad'],
    ['Serie (VIN)', String(v?.vin || '').trim(), { mono: true }],
  ];
  if (String(v?.descripcion_unidad || '').trim()) unidad.push(['Descripción B.PRO', String(v.descripcion_unidad).trim()]);
  const servicio = [
    ['Sucursal', sucursal || 'Sin sucursal', rawSucursal && rawSucursal !== sucursal ? { title: `En la cartera: ${rawSucursal}` } : undefined],
    ['Asesor de servicio (B.PRO)', asesorOf(v) || 'Sin asesor'],
    ['Último servicio', [ultimo, km].filter(Boolean).join(' · ')],
    ['Próximo servicio', [proximo, estado ? `(${estado})` : '', cadText].filter(Boolean).join(' ')],
  ];
  if (String(v?.prioridad || '').trim()) servicio.push(['Prioridad B.PRO', String(v.prioridad).trim()]);
  if (String(v?.status_cliente || '').trim()) servicio.push(['Estado del cliente (B.PRO)', String(v.status_cliente).trim()]);
  const compra = sucursalLabel(v?.lugar_compra);
  if (compra && compra !== sucursal) servicio.push(['Lugar de compra', compra]);
  const contacto = [
    [tr('postventa.lastContact', 'Último contacto'), ultimoContacto],
    ['Último resultado', latestOutcomeLabel(t, v)],
    ['Intentos registrados', hist ? String(hist) : 'Ninguno'],
  ];
  if (v?.handoff_requested) contacto.push(['Asesor solicitado', 'Sí — el cliente pidió hablar con un asesor']);
  return [
    { key: 'cliente', title: 'Cliente', rows: cliente },
    { key: 'unidad', title: 'Unidad', rows: unidad },
    { key: 'servicio', title: 'Servicio', rows: servicio },
    { key: 'cita', title: 'Cita', rows: [['Cita registrada', cita || 'Sin cita registrada']] },
    { key: 'contacto', title: 'Contacto', rows: contacto },
  ];
}

// CampaignDetail focus matcher (feat-calendar-ux-sep3 deep link).
const normMxPhone = (p) => {
  const digits = String(p || '').replace(/[^0-9]/g, '');
  if (!digits) return '';
  const deMobile = digits.replace(/^521(\d{10})$/, '52$1');
  return deMobile.length > 10 ? deMobile.slice(-10) : deMobile;
};

function PostventaPage() {
  const t = usePostventaT();
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link one-shot guard: only act on a given ?vin/?phone key once, so the
  // URL-strip that follows doesn't re-open the drawer (feat-calendar-ux-sep3).
  const deepLinkKeyRef = useRef(null);
  const directLookupVinRef = useRef(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [dueFilter, setDueFilter] = useState('all'); // all | due | upcoming
  const [sortKey, setSortKey] = useState('overdue');
  const [sortDir, setSortDir] = useState('asc');
  // Rendered-row window (see pageWindow above). Page resets to 0 whenever the
  // filtered/sorted set changes so the user never lands on an empty page.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(POSTVENTA_PAGE_SIZE_DEFAULT);
  const [selected, setSelected] = useState(null); // drawer vehicle
  // PERF (2026-10-10, perf-analytics-page-9s-and-postventa-page-6s): the list
  // now arrives as a SLIM projection (only the columns the table renders), so
  // when the drawer opens we fetch the FULL row (GET /postventa/vehicles/{vin})
  // and merge it over the slim one. The drawer renders immediately from the
  // slim row; the extra fields (versión / transmisión / km / enrichment…)
  // fill in when the fetch lands. Fail-soft: an error leaves the slim row.
  // selectedVinRef guards against a late response for a previously-opened VIN.
  const selectedVinRef = useRef(null);
  useEffect(() => {
    const vin = String(selected?.vin || '').trim();
    selectedVinRef.current = vin || null;
    if (!vin || selected?._full) return undefined;
    if (typeof getPostventaVehicle !== 'function') return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => getPostventaVehicle(vin))
      .then((r) => {
        const full = r?.data;
        if (cancelled || !full || String(full.vin || '').trim() !== vin) return;
        if (selectedVinRef.current !== vin) return;
        setSelected((s) => (s && s.vin === vin ? { ...s, ...full, _full: true } : s));
      })
      .catch(() => {
        /* keep the slim row; the drawer already rendered from it */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.vin]);
  const [editProfiles, setEditProfiles] = useState(null); // vehicle whose contact profiles are being edited
  const [exporting, setExporting] = useState(false);
  const [locationFilter, setLocationFilter] = useState('all'); // 'all' | <location>
  // Test / QA rows hidden by default (badge + toggle; persisted per browser so
  // a tester flips it once). Fail-soft around storage access.
  const [showTestRows, setShowTestRows] = useState(() => {
    try {
      return window.localStorage.getItem(SHOW_TEST_ROWS_STORAGE_KEY) === '1';
    } catch (_e) {
      return false;
    }
  });
  const toggleShowTestRows = () =>
    setShowTestRows((cur) => {
      const next = !cur;
      try {
        window.localStorage.setItem(SHOW_TEST_ROWS_STORAGE_KEY, next ? '1' : '0');
      } catch (_e) {
        /* storage disabled — remembering is best-effort */
      }
      return next;
    });
  // ticket v2: Seguimiento chip ('all' | one of SEGUIMIENTO_FILTERS keys).
  const [seguimientoFilter, setSeguimientoFilter] = useState('all');
  // P0 PEN-primary (oct23): fuera-de-planta rows hidden by default (never dialable);
  // "Meses sin visita" band select ('all' | VISITA_BANDS key).
  const [showFueraPlanta, setShowFueraPlanta] = useState(false);
  const [visitaFilter, setVisitaFilter] = useState('all');
  const [managedLocations, setManagedLocations] = useState([]); // admin-curated (Equipo)

  // Outreach modal state.
  const [outreach, setOutreach] = useState(null); // { vins: [...] } | null
  const [agents, setAgents] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [callerIds, setCallerIds] = useState([]);
  const [launching, setLaunching] = useState(false);
  // SINGLE-ATTEMPT OPT-IN (sep6): "Un solo intento (sin reintentos)". Default
  // off preserves the existing 3-attempt behavior; when checked the backend
  // provisions the campaign with max_retries=1.
  const [singleAttempt, setSingleAttempt] = useState(false);
  // Paso 3 confirm dialog (Roger 2026-09-04): before opening the outreach
  // modal, summarize n clientes · agente · canal · modo piloto and require an
  // explicit "Confirmar". { vins, asesorName, source } | null. source is
  // 'selection' (the selection-bar button) or 'all-due' (the guarded
  // "Contactar todos los vencidos…" action) — only for copy.
  const [pendingContact, setPendingContact] = useState(null);
  // MASS-LAUNCH CAP (oct23): the Paso 3 "Entiendo que se contactará a N
  // clientes" acknowledgement; reset every time the confirm dialog opens.
  const [massAck, setMassAck] = useState(false);
  // Header "[Asignar ejecutivo] ▾" panel (Roger 2026-09-04 header cleanup):
  // bulk-assign the current selection + "Gestionar ejecutivos" catalog link,
  // all from one header control. Replaces the old selection-bar "Gestionar ▾"
  // menu AND the removed "Acciones ▾" / "Enviar recordatorios" controls.
  const [showAsignarEjecutivo, setShowAsignarEjecutivo] = useState(false);
  // POSTVENTA-PILOT-CALL-GATE (sep3): when the tenant has postventa pilot mode
  // ON, calls are gated exactly like WhatsApp sends — only numbers on the
  // whitelist are dialed. Fetch the flag (fail-soft) so the outreach modal can
  // show a small "Modo piloto" note; the backend is the hard gate regardless.
  const [pilotMode, setPilotMode] = useState(false);

  // Asesor-scoped targeting (Roger 2026-09-03). "Contactar como": pick MY child
  // clients (or sin-asesor / hand-picked). asesores = tenant mapping
  // [{asesor_name, user_email}]; myAsesor = the logged-in user's row;
  // contactarComo = the asesor this outreach runs AS (defaults to
  // myAsesor.asesor_name, admins can pick any); asesorScope = the active chip
  // (all|me|none|me+none); selectedVins = manual multi-select.
  //
  // Roger addendum (2026-09-03 01:10 CST): NO asesor->child_agent auto-select
  // ("it can fail"). The agent picker stays MANUAL + required with no default;
  // "Contactar como" only drives the scope chips and the asesor_name /
  // initiated_by_user stamped on outreach records.
  const [asesores, setAsesores] = useState([]);
  const [myAsesor, setMyAsesor] = useState(null);
  const [contactarComo, setContactarComo] = useState('');
  const [asesorScope, setAsesorScope] = useState('all');
  const [selectedVins, setSelectedVins] = useState(() => new Set());

  // Ejecutivo de citas (Toyota 2026-09-03): appointment-executive catalog +
  // per-client assignment. ejecutivos = tenant catalog [{nombre,email?,...}];
  // myEjecutivo = the logged-in user's row (for the "Mis clientes (ejecutivo)"
  // chip); ejecutivoScope = active chip ('all' | 'me'); assigningEjecutivo =
  // network in-flight guard for row/bulk assign.
  const [ejecutivos, setEjecutivos] = useState([]);
  const [myEjecutivo, setMyEjecutivo] = useState(null);
  const [ejecutivoScope, setEjecutivoScope] = useState('all');
  const [assigningEjecutivo, setAssigningEjecutivo] = useState(false);
  // Bulk-assign selector value (the ejecutivo picked in the selection bar).
  const [bulkEjecutivo, setBulkEjecutivo] = useState('');

  // -------------------------------------------------------------------------
  // Manual WhatsApp reminder send: the mass "Enviar recordatorios" trigger was
  // REMOVED from this page (Roger 2026-09-04) — postventa contact is Paso 1-2-3
  // only. The send-due backend endpoint + its API client remain intact for a
  // future admin-only surface (Configuración → Postventa), so this page no
  // longer keeps reminder state.

  // Cartera freshness badge (branch 2b): the cartera is a HUMAN Exata export,
  // so we cannot auto-refresh it — instead we surface "Cartera actualizada hace
  // X días" (red when older than the backend threshold) so staleness is visible.
  const [freshness, setFreshness] = useState(null); // { cartera, service_refresh, stale }

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      // Explicit client links use the existing tenant-scoped GET, not the
      // sparse due-date index. Undated manual profiles stay out of cadence.
      const linkedVin = (searchParams.get('vin') || '').trim();
      if (linkedVin) {
        const linked = (await getPostventaVehicle(linkedVin))?.data;
        if (linked?.vin !== linkedVin) throw new Error('Vehicle not found');
        directLookupVinRef.current = linkedVin;
        setSearch(linkedVin);
        // Already the FULL row (tenant-scoped GET): flag it so the drawer's
        // full-row hydration does not fetch the same VIN a second time.
        setVehicles([{ ...linked, _full: true }]);
      } else {
        const res = await listPostventaVehicles();
        setVehicles(res?.data?.vehicles || []);
      }
    } catch (e) {
      setError('No se pudo cargar el cliente solicitado. Verifica tu organización e inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Deep-link from the Calendar cita panel: /postventa?vin=<vin>&phone=<digits>
  // (feat-calendar-ux-sep3). Once the cartera is loaded, find the matching
  // vehicle and OPEN ITS DRAWER (the drawer is where the transcript history +
  // WhatsApp thread live), so "Ver cliente" lands the operator on exactly the
  // client's conversation. Match by VIN first, then by canonicalized phone. If
  // nothing matches (client not in the loaded cartera page), fall back to
  // seeding the search box with the phone/vin so the row surfaces. One-shot per
  // key; strips the params afterward so a refresh/close doesn't re-fire.
  useEffect(() => {
    const vinParam = (searchParams.get('vin') || '').trim();
    const phoneParam = (searchParams.get('phone') || '').trim();
    const key = `${vinParam}|${phoneParam}`;
    if (key === '|') return;
    if (loading || !vehicles.length) return;
    if (deepLinkKeyRef.current === key) return;
    deepLinkKeyRef.current = key;
    const wantPhone = normMxPhone(phoneParam);
    const match = vehicles.find((v) => {
      if (vinParam && String(v.vin || '').trim() === vinParam) return true;
      if (wantPhone && normMxPhone(v.telefono) === wantPhone) return true;
      return false;
    });
    if (match) {
      setSelected(match);
    } else {
      // Not on the loaded list -> surface it via search (VIN preferred, else phone).
      setSearch(vinParam || phoneParam);
    }
    // Strip the deep-link params so the drawer/search state is now user-owned.
    const next = new URLSearchParams(searchParams);
    // Keep direct-lookup links reloadable: this row may not be in the due index.
    if (directLookupVinRef.current !== vinParam) next.delete('vin');
    next.delete('phone');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, loading, searchParams]);

  useEffect(() => {
    load();
    // Managed (admin-curated) locations from Equipo. Fail-soft: if the endpoint
    // isn't deployed yet, the dropdown falls back to locations seen in the data.
    getPostventaLocations()
      .then((r) => setManagedLocations(r?.data?.locations || []))
      .catch(() => setManagedLocations([]));
    // Asesor mapping + "who am I". Fail-soft: if the endpoint isn't deployed,
    // the selector/chips still work by name off the cartera rows themselves.
    if (typeof getPostventaAsesores === 'function') {
      Promise.resolve(getPostventaAsesores())
        .then((r) => {
          const list = r?.data?.asesores || [];
          setAsesores(list);
          const mine = r?.data?.my_asesor || null;
          setMyAsesor(mine);
          // Default "Contactar como" to the logged-in user's asesor when known.
          if (mine?.asesor_name) setContactarComo(mine.asesor_name);
        })
        .catch(() => {
          setAsesores([]);
          setMyAsesor(null);
        });
    }
    // Ejecutivos de citas catalog + "who am I". Fail-soft: absent endpoint
    // leaves the assign menus empty (no crash).
    if (typeof getPostventaEjecutivosCitas === 'function') {
      Promise.resolve(getPostventaEjecutivosCitas())
        .then((r) => {
          const cat = r?.data?.ejecutivos || [];
          const mineEj = r?.data?.my_ejecutivo || null;
          setEjecutivos(cat);
          setMyEjecutivo(mineEj);
          // Default "Ejecutivo de citas (quien da seguimiento)" to the logged-in
          // user WHEN they are in the ejecutivos catalog (Roger 2026-09-04). We
          // only set it if the asesor-mapping default above did not already
          // seed a value, so we never clobber an explicit asesor default.
          const myName = String(mineEj?.nombre || '').trim();
          const inCatalog =
            !!myName &&
            cat.some(
              (e) =>
                String(e?.nombre || '').trim().toLowerCase() ===
                myName.toLowerCase()
            );
          if (inCatalog) {
            setContactarComo((cur) => (cur ? cur : myName));
          }
        })
        .catch(() => {
          setEjecutivos([]);
          setMyEjecutivo(null);
        });
    }
    // Cartera freshness. Fail-soft: if the endpoint isn't deployed yet (or the
    // API helper is absent in a partial bundle), the badge simply doesn't
    // render (no crash, no blocking).
    if (typeof getPostventaFreshness === 'function') {
      Promise.resolve(getPostventaFreshness())
        .then((r) => setFreshness(r?.data?.tenant || null))
        .catch(() => setFreshness(null));
    }
    // Preload agents + caller numbers for the outreach picker.
    api
      .get('/agents')
      .then((r) => setAgents(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    listPhoneNumbers()
      .then((r) => setNumbers(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    // Postventa pilot mode flag (fail-soft): drives the outreach modal note.
    // POSTVENTA-PILOT-STATUS (oct10): read the ROLE-OPEN status endpoint, NOT
    // /tenants/me/whatsapp-config — that one is admin/super_admin-only (unmasked
    // WABA fields), so managers got a 403 on every Postventa load and the
    // catch() silently hid the pilot note while the tenant was actually ON.
    api
      .get('/tenants/me/postventa-pilot-status')
      .then((r) => setPilotMode(!!r?.data?.postventa_pilot_mode))
      .catch(() => setPilotMode(false));
  }, []);

  const toggleCaller = (num) =>
    setCallerIds((cur) =>
      cur.includes(num) ? cur.filter((n) => n !== num) : [...cur, num]
    );

  // Toggle one VIN in the manual multi-select set.
  const toggleSelectVin = (vin) =>
    setSelectedVins((cur) => {
      const next = new Set(cur);
      if (next.has(vin)) next.delete(vin);
      else next.add(vin);
      return next;
    });

  // The logged-in user's asesor_name (used for the "me" scope chips + stamping).
  const myAsesorName = String(myAsesor?.asesor_name || '').trim();

  // The logged-in user's email (best-effort, fail-soft): prefer the asesor
  // mapping row, fall back to the ejecutivo row. Used to scope the remembered
  // Agente IA choice per user so one user's last pick never leaks to another.
  const currentUserEmail = String(
    myAsesor?.user_email || myEjecutivo?.email || myEjecutivo?.user_email || ''
  ).trim();

  // Paso 1 agent options, grouped so POSTVENTA agents come first and every other
  // department is collapsed under "Otros" (Roger 2026-09-04 clarity fix). A
  // ventas agent is therefore never the obvious/first choice on this page.
  const groupedAgents = useMemo(() => groupAgentsByDepartment(agents), [agents]);
  // "Otros" (non-postventa) group starts COLLAPSED so it never distracts; the
  // user opens it explicitly if they really want a ventas/cobranza agent.
  const [showOtrosAgents, setShowOtrosAgents] = useState(false);
  // Edge case: a tenant with NO postventa agents would otherwise see an empty
  // picker (everything hidden under a collapsed "Otros"). When there is nothing
  // to hide behind — no postventa agents — auto-open "Otros" so the list isn't
  // mysteriously empty. Runs once after agents load; the user can still toggle.
  const otrosAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (otrosAutoOpenedRef.current) return;
    if (!agents || !agents.length) return;
    otrosAutoOpenedRef.current = true;
    if (groupedAgents.postventa.length === 0 && groupedAgents.otros.length > 0) {
      setShowOtrosAgents(true);
    }
  }, [agents, groupedAgents]);

  // Remember the last Agente IA choice PER USER (localStorage), and restore it
  // when the agents list loads — but NEVER auto-apply a remembered VENTAS agent
  // on this postventa page (Roger: "never auto-pick a ventas agent"). Only a
  // remembered POSTVENTA agent that still exists is re-applied; a ventas one is
  // ignored so the field stays on its placeholder. Fail-soft around storage
  // access (private mode / disabled storage throws).
  const lastAgentRestoredRef = useRef(false);
  useEffect(() => {
    if (lastAgentRestoredRef.current) return; // one-shot
    if (!agents || !agents.length) return; // wait for the list
    lastAgentRestoredRef.current = true;
    let remembered = '';
    try {
      remembered = window.localStorage.getItem(
        lastAgentStorageKey(currentUserEmail)
      ) || '';
    } catch (_e) {
      remembered = '';
    }
    if (!remembered) return;
    const match = agents.find((a) => a && a.id === remembered) || null;
    // Only re-apply a POSTVENTA agent; a remembered ventas agent is ignored so
    // the placeholder stays and the user must consciously open "Otros".
    if (match && isPostventaDept(match.department)) setAgentId(remembered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, currentUserEmail]);

  // Persist the chosen Agente IA per user whenever it changes (any department:
  // we store the raw choice; the restore step is the one that refuses to
  // auto-apply a ventas agent). Fail-soft around storage access.
  useEffect(() => {
    if (!agentId) return;
    try {
      window.localStorage.setItem(
        lastAgentStorageKey(currentUserEmail),
        agentId
      );
    } catch (_e) {
      /* storage disabled — remembering is best-effort */
    }
  }, [agentId, currentUserEmail]);

  // Paso 1: the agent currently chosen in the top-of-list "Agente IA" selector.
  // Resolved from the loaded agents list so we can ALWAYS show its name +
  // department badge ("Agente: Postventa Chevrolet") — Roger's core ask: the
  // user must always see who will call/write. null until one is picked.
  const chosenAgent = useMemo(
    () => (agents || []).find((a) => a && a.id === agentId) || null,
    [agents, agentId]
  );

  // Does a row belong to the logged-in user's asesor? Name-first (cartera),
  // then advisor_id against the mapping when a name isn't on the row.
  const isMine = (v) => {
    const n = asesorOf(v).toLowerCase();
    return !!myAsesorName && n === myAsesorName.toLowerCase();
  };
  const isUnassigned = (v) => !asesorOf(v);

  // Apply the active asesor scope chip to a vehicle list.
  const applyAsesorScope = (list) => {
    if (asesorScope === 'me') return list.filter(isMine);
    if (asesorScope === 'none') return list.filter(isUnassigned);
    if (asesorScope === 'me+none')
      return list.filter((v) => isMine(v) || isUnassigned(v));
    return list; // 'all'
  };

  // The logged-in user's ejecutivo name (for the "Mis clientes (ejecutivo)" chip).
  const myEjecutivoName = String(myEjecutivo?.nombre || '').trim();
  const isMineEjecutivo = (v) => {
    const n = ejecutivoOf(v).toLowerCase();
    return !!myEjecutivoName && n === myEjecutivoName.toLowerCase();
  };
  // Apply the active ejecutivo scope chip.
  const applyEjecutivoScope = (list) => {
    if (ejecutivoScope === 'me') return list.filter(isMineEjecutivo);
    return list; // 'all'
  };

  // Assign (or clear with '') the ejecutivo de citas on one or many VINs.
  // Optimistically patches the loaded rows, then persists; reloads on error to
  // resync. Single-row menu passes [vin]; bulk passes the selection.
  // oct20 CADENCE CAP (Sergio): "Reanudar contacto" on a vehicle the cadence
  // stopped after N cycles without contact. The backend resets the counter
  // (logs who/when), re-queues the stopped dial rows and resumes their
  // campaign; the row/drawer update from the response.
  const [relaunchingVin, setRelaunchingVin] = useState('');
  const relaunchFollowup = async (vehicle) => {
    const vin = String(vehicle?.vin || '').trim();
    if (!vin || relaunchingVin) return;
    if (!window.confirm(`¿Reanudar el contacto automático para ${vehicle?.nombre || vin}? Quedará registrado quién lo reanudó y cuándo.`)) return;
    setRelaunchingVin(vin);
    try {
      const res = await relaunchPostventaFollowup(vin);
      const data = res?.data || {};
      const patch = { followup: data.followup || {}, followup_label: data.label || '' };
      setVehicles((cur) => cur.map((v) => (v.vin === vin ? { ...v, ...patch } : v)));
      setSelected((s) => (s && s.vin === vin ? { ...s, ...patch } : s));
      const requeued = Number(data.requeued) || 0;
      toast.success(
        requeued
          ? `Contacto reanudado: ${requeued} llamada(s) vuelven a la cola de marcación`
          : 'Contacto reanudado: el vehículo vuelve a ser elegible; lánzalo con “Llamar”'
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail : 'No se pudo reanudar el contacto');
    } finally {
      setRelaunchingVin('');
    }
  };

  const assignEjecutivo = async (vins, ejecutivoName) => {
    const list = (vins || []).filter(Boolean);
    if (!list.length) return;
    const name = (ejecutivoName || '').trim();
    setAssigningEjecutivo(true);
    // Optimistic local update.
    setVehicles((cur) =>
      cur.map((v) =>
        list.includes(v.vin) ? { ...v, ejecutivo_citas: name } : v
      )
    );
    try {
      const res = await assignPostventaEjecutivo({
        vins: list,
        ejecutivo_citas: name || undefined,
      });
      const assigned = res?.data?.assigned ?? 0;
      const errs = res?.data?.errors || [];
      if (name) {
        toast.success(
          `Ejecutivo "${name}" asignado a ${assigned} cliente(s)` +
            (errs.length ? ` — ${errs.length} con error` : '')
        );
      } else {
        toast.success(
          `Ejecutivo quitado de ${assigned} cliente(s)` +
            (errs.length ? ` — ${errs.length} con error` : '')
        );
      }
      if (errs.length) load(); // resync rows that failed
    } catch (e) {
      toast.error('No se pudo asignar el ejecutivo');
      load(); // undo optimistic update
    } finally {
      setAssigningEjecutivo(false);
    }
  };

  const launchOutreach = async () => {
    const vins = outreach?.vins || [];
    if (!vins.length) return;
    if (!agentId) {
      toast.error('Selecciona un agente');
      return;
    }
    if (!callerIds.length) {
      toast.error('Selecciona al menos un número');
      return;
    }
    setLaunching(true);
    try {
      const res = await startPostventaOutreach({
        vins,
        agent_id: agentId,
        caller_ids: callerIds,
        // Stamp the "Contactar como" asesor on every contact (cita/CRM/B.PRO
        // write-back + child-agent routing). Blank => per-row fallback.
        asesor_name: (outreach?.asesorName || contactarComo || '').trim() || undefined,
        // Only sent when the user opts in; omitted (=> backend default 3) otherwise.
        single_attempt: singleAttempt || undefined,
        // MASS-LAUNCH CAP (oct23): only sent after the explicit Paso 3
        // acknowledgement on a > cap selection. The backend honors it for
        // admins only; everyone else still gets 400 max_items_exceeded.
        allow_mass: outreach?.allowMass ? true : undefined,
      });
      if (res?.data?.success) {
        const skipped = res.data.skipped_no_phone || [];
        // POSTVENTA-PILOT-CALL-GATE (sep3): numbers not on the pilot whitelist
        // are NOT dialed while pilot mode is on. Surface them as an explicit
        // "omitido por modo piloto" toast instead of silently dropping them.
        const skippedPilot = res.data.skipped_pilot || [];
        const contacts = res.data.contacts || 0;
        if (skippedPilot.length && contacts === 0) {
          // Everything selected was blocked by the pilot whitelist.
          toast.error(
            `Omitido por modo piloto: ${skippedPilot.length} vehiculo(s) no autorizado(s); ` +
              'no se marco a nadie'
          );
        } else {
          toast.success(
            `Contacto iniciado: ${contacts} vehiculo(s)` +
              (skipped.length ? ` — ${skipped.length} sin telefono` : '') +
              (skippedPilot.length
                ? ` — ${skippedPilot.length} omitido(s) por modo piloto`
                : '')
          );
        }
        setOutreach(null);
        load();
      } else {
        toast.error('No se pudo iniciar el contacto');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo iniciar el contacto');
    } finally {
      setLaunching(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  const rows = useMemo(() => {
    let list = [...(vehicles || [])];
    const q = search.trim().toLowerCase();
    // Test / QA rows are hidden unless the "Mostrar pruebas" toggle is on. An
    // explicit lookup (calendar deep link ?vin=, or a search that IS a row's
    // VIN / phone) always reaches its row — a tester following a link must
    // never land on an empty table.
    const explicitLookup =
      !!directLookupVinRef.current ||
      !!(searchParams.get('vin') || '').trim() ||
      (q.length >= 8 && list.some((v) =>
        String(v.vin || '').toLowerCase() === q || normMxPhone(v.telefono) === normMxPhone(q)));
    if (!showTestRows && !explicitLookup) list = list.filter((v) => !isTestRow(v));
    // Fuera de planta (> 96 meses / modelo fuera de ventana): hidden unless the
    // toggle is on; an explicit VIN/phone lookup still reaches the row (badged).
    if (!showFueraPlanta && !explicitLookup) list = list.filter((v) => !isFueraDePlanta(v));
    // "Meses sin visita" band chip: membership in the stamped overlapping bands.
    if (visitaFilter !== 'all') list = list.filter((v) => rowInVisitaBand(v, visitaFilter));
    if (q) {
      list = list.filter((v) =>
        [v.vin, v.modelo, v.nombre, v.telefono, v.advisor_id, v.advisor_name, v.ejecutivo_citas,
          v.anio, v.version, rowSucursal(v)]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q))
      );
    }
    // Asesor scope chip (Mis clientes / Sin asesor / Mis clientes + sin asesor).
    list = applyAsesorScope(list);
    // Ejecutivo-de-citas scope chip ("Mis clientes (ejecutivo)").
    list = applyEjecutivoScope(list);
    if (dueFilter === 'due') {
      list = list.filter((v) => String(v.next_service_due || '') <= today);
    } else if (dueFilter === 'upcoming') {
      list = list.filter((v) => String(v.next_service_due || '') > today);
    }
    // Location filter: narrow to a single sucursal (servicio, fallback compra).
    if (locationFilter !== 'all') {
      if (locationFilter === '__none__') {
        list = list.filter((v) => !locationOf(v));
      } else {
        list = list.filter((v) => locationOf(v) === locationFilter);
      }
    }
    // Seguimiento chip: bucket of the derived state (null states never match).
    if (seguimientoFilter !== 'all') {
      list = list.filter((v) => seguimientoBucket(seguimientoOf(v)?.state) === seguimientoFilter);
    }
    list.sort((a, b) => {
      let cmp;
      if (sortKey === 'seguimiento') {
        const ai = SEGUIMIENTO_STATES.indexOf(seguimientoOf(a)?.state);
        const bi = SEGUIMIENTO_STATES.indexOf(seguimientoOf(b)?.state);
        if (ai < 0 && bi < 0) cmp = 0;
        else if (ai < 0) return 1; // nulls always last
        else if (bi < 0) return -1;
        else cmp = ai - bi;
      } else if (sortKey === 'overdue') {
        // Sort by numeric days-overdue (most overdue first when asc); null dates last.
        const ad = daysOverdue(a);
        const bd = daysOverdue(b);
        if (ad == null && bd == null) cmp = 0;
        else if (ad == null) return 1; // nulls always last regardless of dir
        else if (bd == null) return -1;
        else cmp = bd - ad; // larger overdue first
      } else if (sortKey === 'intentos') {
        const ac = Array.isArray(a.contact_history) ? a.contact_history.length : 0;
        const bc = Array.isArray(b.contact_history) ? b.contact_history.length : 0;
        cmp = ac - bc;
      } else if (sortKey === 'ultimo_contacto') {
        // Sort by most-recent contact timestamp; never-contacted sorts oldest.
        const al = lastContact(a);
        const bl = lastContact(b);
        cmp = tsKey(al?.ts) - tsKey(bl?.ts);
      } else if (sortKey === 'ultimo_servicio') {
        // Mixed ISO / dd-mm-yyyy strings: compare as dates, blanks last.
        const ak = serviceDateKey(a.ultimo_servicio);
        const bk = serviceDateKey(b.ultimo_servicio);
        if (ak === -Infinity && bk === -Infinity) cmp = 0;
        else if (ak === -Infinity) return 1;
        else if (bk === -Infinity) return -1;
        else cmp = ak - bk;
      } else if (sortKey === 'sucursal') {
        cmp = rowSucursal(a).localeCompare(rowSucursal(b), 'es', { sensitivity: 'base' });
      } else if (sortKey === 'ejecutivo') {
        cmp = ejecutivoOf(a).localeCompare(ejecutivoOf(b), 'es', { sensitivity: 'base' });
      } else {
        const av = String(a[sortKey] ?? '');
        const bv = String(b[sortKey] ?? '');
        cmp = av.localeCompare(bv, undefined, { numeric: true });
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vehicles,
    showTestRows,
    search,
    dueFilter,
    locationFilter,
    seguimientoFilter,
    showFueraPlanta,
    visitaFilter,
    asesorScope,
    myAsesorName,
    ejecutivoScope,
    myEjecutivoName,
    sortKey,
    sortDir,
    today,
  ]);

  // How many loaded rows are test / QA rows (badge + toggle count).
  const testRowCount = useMemo(
    () => (vehicles || []).filter(isTestRow).length,
    [vehicles]
  );
  // How many loaded rows are "fuera de planta" (toggle count).
  const fueraPlantaCount = useMemo(
    () => (vehicles || []).filter(isFueraDePlanta).length,
    [vehicles]
  );
  // Hidden test rows that the current search WOULD match — so an empty result
  // can say "activa Mostrar pruebas" instead of leaving the tester puzzled.
  const hiddenTestMatches = useMemo(() => {
    if (showTestRows) return 0;
    const q = search.trim().toLowerCase();
    if (!q) return 0;
    return (vehicles || []).filter((v) =>
      isTestRow(v) &&
      [v.vin, v.modelo, v.nombre, v.telefono, v.ejecutivo_citas, v.anio, v.version, rowSucursal(v)]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q))
    ).length;
  }, [vehicles, showTestRows, search]);

  // ticket v2: per-chip Seguimiento counts over the rows every OTHER filter
  // yields (search / asesor / ejecutivo / due / location) BEFORE the
  // Seguimiento chip itself, overdue rows only -- same bucket map as the API.
  const seguimientoCounts = useMemo(() => {
    let base = [...(vehicles || [])];
    if (!showTestRows) base = base.filter((v) => !isTestRow(v));
    if (!showFueraPlanta) base = base.filter((v) => !isFueraDePlanta(v));
    const q = search.trim().toLowerCase();
    if (q) {
      base = base.filter((v) =>
        [v.vin, v.modelo, v.nombre, v.telefono, v.advisor_id, v.advisor_name, v.ejecutivo_citas,
          v.anio, v.version, rowSucursal(v)]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q))
      );
    }
    base = applyAsesorScope(base);
    base = applyEjecutivoScope(base);
    if (dueFilter === 'due') {
      base = base.filter((v) => String(v.next_service_due || '') <= today);
    } else if (dueFilter === 'upcoming') {
      base = base.filter((v) => String(v.next_service_due || '') > today);
    }
    if (locationFilter !== 'all') {
      base =
        locationFilter === '__none__'
          ? base.filter((v) => !locationOf(v))
          : base.filter((v) => locationOf(v) === locationFilter);
    }
    const counts = { sin_iniciar: 0, total: 0 };
    SEGUIMIENTO_FILTERS.forEach((f) => { counts[f.key] = 0; });
    base.forEach((v) => {
      const st = seguimientoOf(v);
      if (!st) return;
      counts.total += 1;
      const bucket = seguimientoBucket(st.state);
      if (bucket) counts[bucket] += 1;
      else if (st.state === 'sin_iniciar') counts.sin_iniciar += 1;
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, showTestRows, showFueraPlanta, search, dueFilter, locationFilter, asesorScope, myAsesorName, ejecutivoScope, myEjecutivoName, today]);

  // Per-chip counts (from the search/due/location-filtered set, BEFORE the
  // asesor scope is applied) so each badge shows how many rows that chip yields.
  const asesorCounts = useMemo(() => {
    let base = [...(vehicles || [])];
    if (!showTestRows) base = base.filter((v) => !isTestRow(v));
    if (!showFueraPlanta) base = base.filter((v) => !isFueraDePlanta(v));
    const q = search.trim().toLowerCase();
    if (q) {
      base = base.filter((v) =>
        [v.vin, v.modelo, v.nombre, v.telefono, v.advisor_id, v.advisor_name, v.ejecutivo_citas,
          v.anio, v.version, rowSucursal(v)]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q))
      );
    }
    if (dueFilter === 'due') {
      base = base.filter((v) => String(v.next_service_due || '') <= today);
    } else if (dueFilter === 'upcoming') {
      base = base.filter((v) => String(v.next_service_due || '') > today);
    }
    if (locationFilter !== 'all') {
      base =
        locationFilter === '__none__'
          ? base.filter((v) => !locationOf(v))
          : base.filter((v) => locationOf(v) === locationFilter);
    }
    const mine = base.filter(isMine).length;
    const none = base.filter(isUnassigned).length;
    return {
      all: base.length,
      me: mine,
      none,
      'me+none': base.filter((v) => isMine(v) || isUnassigned(v)).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, showTestRows, showFueraPlanta, search, dueFilter, locationFilter, myAsesorName, today]);

  // How many loaded clients currently have an ejecutivo de citas assigned.
  // Drives the Paso 1 empty-state callout: when the tenant has 0 ejecutivos in
  // its catalog OR 0 clients assigned, "Mis clientes 0" is otherwise unexplained
  // (Roger 2026-09-04). Counted over ALL loaded rows (not the filtered view) so
  // the callout reflects the tenant, not the current search.
  const assignedEjecutivoCount = useMemo(
    () => (vehicles || []).filter((v) => ejecutivoOf(v)).length,
    [vehicles]
  );
  // Show the callout only once data has loaded and the tenant genuinely has no
  // ejecutivos catalog OR nobody assigned yet (and there ARE clients to assign).
  const showEjecutivoEmptyState =
    !loading &&
    (vehicles || []).length > 0 &&
    (ejecutivos.length === 0 || assignedEjecutivoCount === 0);

  // Only this window of `rows` is mounted in the DOM.
  const pageInfo = useMemo(() => pageWindow(rows, page, pageSize), [rows, page, pageSize]);
  useEffect(() => {
    setPage(0);
  }, [search, dueFilter, locationFilter, seguimientoFilter, asesorScope, ejecutivoScope, sortKey, sortDir, pageSize]);
  // VINs on the CURRENT PAGE only — what the header checkbox selects.
  // MASS-LAUNCH CAP (oct23): this used to be every filtered row (`rows`), so
  // one click on the header checkbox selected 1313 vencidos -> "Iniciar
  // contacto (1313)" (a de-facto "llamar a todos" one mis-click away). The
  // header checkbox is now "Seleccionar página": it only ever adds/removes
  // the rows mounted in front of the user.
  const pageVins = useMemo(() => pageInfo.items.map((v) => v.vin), [pageInfo]);
  const selectedPageCount = useMemo(
    () => pageVins.filter((vin) => selectedVins.has(vin)).length,
    [pageVins, selectedVins]
  );
  const allPageSelected =
    pageVins.length > 0 && selectedPageCount === pageVins.length;

  const toggleSelectPage = () =>
    setSelectedVins((cur) => {
      const next = new Set(cur);
      if (allPageSelected) {
        pageVins.forEach((vin) => next.delete(vin));
      } else {
        pageVins.forEach((vin) => next.add(vin));
      }
      return next;
    });
  // Over the per-launch cap? Drives the selection-bar warning + the Paso 3
  // acknowledgement gate (see POSTVENTA_OUTREACH_MAX_ITEMS).
  const selectionOverCap = selectedVins.size > POSTVENTA_OUTREACH_MAX_ITEMS;

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Funnel stat cards from the UNFILTERED vehicle list, mirroring backend
  // /postventa/metrics predicates (web_api_server.py). Citas = has last_appointment
  // (derived here honestly; not part of the backend metrics endpoint).
  const stats = useMemo(() => {
    const list = vehicles || [];
    return {
      total: list.length,
      vencidos: list.filter((v) => {
        const d = daysOverdue(v);
        return d != null && d >= 0;
      }).length,
      contactados: list.filter(
        (v) => Array.isArray(v.contact_history) && v.contact_history.length > 0
      ).length,
      citas: list.filter((v) => v.last_appointment).length,
    };
  }, [vehicles]);

  // Location dropdown options: the admin-curated (Equipo) locations UNION every
  // location actually seen in the data (servicio, falling back to compra) — so
  // freshly-imported places still appear even before an admin lists them.
  // Sorted alpha; a "Sin ubicación" option is appended only if some vehicle has
  // no location at all.
  const locationOptions = useMemo(() => {
    const set = new Set();
    let hasBlank = false;
    for (const m of managedLocations || []) {
      const name = sucursalLabel(m);
      if (name) set.add(name);
    }
    for (const v of vehicles || []) {
      const loc = locationOf(v);
      if (loc) set.add(loc);
      else hasBlank = true;
    }
    const opts = Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    if (hasBlank) opts.push('__none__');
    return opts;
  }, [vehicles, managedLocations]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportPostventaVehicles();
      const blob = new Blob([res.data], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'postventa.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('No se pudo exportar la cartera. Intenta de nuevo.');
    } finally {
      setExporting(false);
    }
  };

  // NOTE (Roger 2026-09-04): the mass "Enviar recordatorios" preview+send
  // handlers were REMOVED from this page along with their UI; postventa contact
  // is Paso 1-2-3 only. The send-due backend endpoint + its API client stay
  // intact for a future admin-only surface.

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <WrenchScrewdriverIcon className="h-7 w-7 text-teal-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {t('nav.postventa', 'Postventa')}
            </h1>
            <p className="text-sm text-gray-500">
              Clientes con servicio vencido — contáctalos para reactivarlos.
            </p>
            {searchParams.get('vin') && (
              <p className="text-sm text-indigo-700 mt-2">
                Vista de un cliente; los contadores corresponden solo a este perfil.{' '}
                <a href="/postventa" className="underline">Ver toda la cartera</a>
              </p>
            )}
            <CarteraFreshnessBadge freshness={freshness} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* [Asignar ejecutivo] — the ONE ejecutivo control on this page now
              (Roger 2026-09-04 header cleanup). It replaces the selection-bar
              "Gestionar ▾" menu: bulk-assign the current selection AND reach the
              ejecutivo catalog ("Gestionar ejecutivos" → /team) from one place.
              The mass "Enviar recordatorios" / "Acciones ▾ · Contactar vencidos"
              controls were REMOVED from the working page (contact happens only
              via Paso 1-2-3 with the pilot gate); their endpoints/handlers stay
              intact for a future admin-only surface. */}
          <div className="relative" data-testid="postventa-asignar-ejecutivo-menu">
            <button
              type="button"
              data-testid="postventa-asignar-ejecutivo-toggle"
              onClick={() => setShowAsignarEjecutivo((s) => !s)}
              className="inline-flex items-center gap-1.5 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
            >
              <UserGroupIcon className="h-4 w-4" />
              Asignar ejecutivo
              <ChevronDownIcon className="h-4 w-4" />
            </button>
            {showAsignarEjecutivo && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowAsignarEjecutivo(false)}
                />
                <div
                  className="absolute right-0 z-20 mt-1 w-80 rounded-md border border-gray-200 bg-white p-3 shadow-lg"
                  data-testid="postventa-bulk-ejecutivo"
                >
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Asignar ejecutivo de citas a la selección
                  </label>
                  <p className="mb-2 text-xs text-gray-500">
                    {selectedVins.size > 0
                      ? `${selectedVins.size} cliente(s) seleccionado(s).`
                      : 'Selecciona clientes en la tabla para asignarlos.'}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <select
                      value={bulkEjecutivo}
                      onChange={(e) => setBulkEjecutivo(e.target.value)}
                      className="flex-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800"
                    >
                      <option value="">Ejecutivo de citas…</option>
                      {ejecutivos.map((ej) => (
                        <option key={ej.nombre} value={ej.nombre}>
                          {ej.nombre}
                        </option>
                      ))}
                      <option value="__clear__">— Quitar ejecutivo —</option>
                    </select>
                    <button
                      type="button"
                      data-testid="postventa-assign-ejecutivo"
                      disabled={
                        assigningEjecutivo ||
                        !bulkEjecutivo ||
                        selectedVins.size === 0
                      }
                      onClick={() => {
                        const vins = Array.from(selectedVins);
                        if (!vins.length || !bulkEjecutivo) return;
                        const name =
                          bulkEjecutivo === '__clear__' ? '' : bulkEjecutivo;
                        assignEjecutivo(vins, name);
                        setShowAsignarEjecutivo(false);
                      }}
                      className="rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                    >
                      Asignar
                    </button>
                  </div>
                  <div className="mt-2 border-t border-gray-100 pt-2">
                    <Link
                      to="/team"
                      data-testid="postventa-gestionar-ejecutivos-catalog"
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                      onClick={() => setShowAsignarEjecutivo(false)}
                    >
                      Gestionar ejecutivos (catálogo)
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded-md bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {exporting ? '…' : t('common.export', 'Exportar')}
          </button>
        </div>
      </div>

      {/* Funnel stat cards (from unfiltered vehicles) */}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          ['Total', stats.total, 'text-gray-900'],
          ['Vencidos', stats.vencidos, 'text-red-600'],
          ['Contactados', stats.contactados, 'text-amber-600'],
          ['Citas', stats.citas, 'text-teal-600'],
        ].map(([label, value, cls]) => (
          <div
            key={label}
            className="rounded-lg border border-gray-200 bg-white px-4 py-3"
          >
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {label}
            </div>
            <div className={`mt-1 text-2xl font-semibold ${cls}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* =====================================================================
          PASO 1 — ¿Quién contacta?  (Roger 2026-09-03/04: one clear top-to-bottom
          sequence.) Two selectors side by side: "Contactar como (ejecutivo de
          citas)" (who I am — drives the scope chips + the asesor_name stamped on
          outreach) and "Agente IA" (REQUIRED, manual, NO auto-select; shows the
          agent name + department badge). The chosen agent is ALWAYS visible so
          the user knows exactly who will call/write — that visibility was the
          missing piece Roger called "too confusing".
          ===================================================================== */}
      <div
        className="mb-4 rounded-lg border border-gray-200 bg-white p-4"
        data-testid="postventa-paso1"
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
            1
          </span>
          <h2 className="text-sm font-semibold text-gray-900">
            ¿Quién contacta?
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Ejecutivo de citas (quien da seguimiento): drives the scope chips +
              the asesor_name stamped on outreach. Defaults to the logged-in user
              when they are in the ejecutivos catalog; admins can pick any. The
              placeholder + "Gestionar ejecutivos" link + helper text explain the
              role so "Sin ejecutivo" is never a mysterious default (Roger
              2026-09-04). */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Ejecutivo de citas (quién da seguimiento)
            </label>
            <select
              data-testid="postventa-contactar-como"
              value={contactarComo}
              onChange={(e) => setContactarComo(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Elegir ejecutivo…</option>
              {asesores.map((a) => (
                <option key={a.asesor_name} value={a.asesor_name}>
                  {a.asesor_name}
                </option>
              ))}
            </select>
            <p
              className="mt-1 text-xs text-gray-500"
              data-testid="postventa-ejecutivo-helper"
            >
              El ejecutivo es la persona (p. ej. Jocelin) que da seguimiento a
              sus clientes; sirve para filtrar “Mis clientes”.{' '}
              <Link
                to="/team"
                className="font-medium text-indigo-600 hover:text-indigo-800"
                data-testid="postventa-gestionar-ejecutivos-link"
              >
                Gestionar ejecutivos
              </Link>
            </p>
          </div>
          {/* Agente IA — REQUIRED, manual, NO auto-select (Roger addendum
              2026-09-03: asesor->child_agent auto-select "can fail"). Roger
              2026-09-04 clarity fix: placeholder is "Elegir agente IA…";
              POSTVENTA agents are listed FIRST under a "Postventa" group; every
              other department is collapsed under "Otros" (opened on demand) so a
              ventas agent is never the obvious/first pick. Each option carries a
              department label. The last choice is remembered per user
              (localStorage), but a remembered ventas agent is never auto-applied
              here. */}
          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              <label className="block text-sm font-medium text-gray-700">
                Agente IA <span className="text-red-500">*</span>
              </label>
              {groupedAgents.otros.length > 0 && (
                <button
                  type="button"
                  data-testid="postventa-agent-toggle-otros"
                  onClick={() => setShowOtrosAgents((s) => !s)}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                >
                  {showOtrosAgents
                    ? 'Ocultar otros departamentos'
                    : `Mostrar otros (${groupedAgents.otros.length})`}
                </button>
              )}
            </div>
            <select
              data-testid="postventa-agent-select"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className={`w-full rounded-md border px-3 py-2 text-sm ${
                agentId ? 'border-gray-300' : 'border-amber-300 bg-amber-50'
              }`}
            >
              <option value="">Elegir agente IA…</option>
              {groupedAgents.postventa.length > 0 && (
                <optgroup label="Postventa">
                  {groupedAgents.postventa.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} — {departmentLabel(a.department)}
                      {a.language ? ` (${a.language})` : ''}
                    </option>
                  ))}
                </optgroup>
              )}
              {/* "Otros" stays collapsed until the user opens it, EXCEPT when it
                  holds the currently-selected agent (so a remembered/picked
                  ventas agent is never orphaned out of the list). */}
              {groupedAgents.otros.length > 0 &&
                (showOtrosAgents ||
                  groupedAgents.otros.some((a) => a.id === agentId)) && (
                  <optgroup label="Otros">
                    {groupedAgents.otros.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} — {departmentLabel(a.department)}
                        {a.language ? ` (${a.language})` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
            </select>
            <p
              className="mt-1 text-xs text-gray-500"
              data-testid="postventa-agent-helper"
            >
              El agente IA es el asistente que llama/escribe al cliente.
            </p>
          </div>
        </div>
        {/* Always-visible chosen-agent line — "Agente: Postventa Chevrolet". */}
        <div className="mt-3 flex items-center gap-2 text-sm" data-testid="postventa-chosen-agent">
          {chosenAgent ? (
            <>
              <span className="font-medium text-gray-700">Agente:</span>
              <span className="font-semibold text-gray-900">{chosenAgent.name}</span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${departmentBadgeClass(
                  chosenAgent.department
                )}`}
              >
                {departmentLabel(chosenAgent.department)}
              </span>
            </>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-amber-700">
              <ExclamationTriangleIcon className="h-4 w-4" />
              Elige un Agente IA para poder iniciar contacto.
            </span>
          )}
        </div>
        {/* Empty-state callout: when the tenant has NO ejecutivos in its catalog
            OR nobody assigned yet, "Mis clientes 0" (Paso 2) is otherwise
            unexplained. Point the user straight at the header
            "[Asignar ejecutivo]" button (Roger 2026-09-04). */}
        {showEjecutivoEmptyState && (
          <div
            className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="postventa-ejecutivo-empty-state"
          >
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              {ejecutivos.length === 0
                ? 'Aún no hay ejecutivos registrados'
                : 'Aún no hay clientes asignados a un ejecutivo'}
              {' — '}
              <Link
                to="/team"
                className="font-medium text-amber-900 underline hover:text-amber-950"
                data-testid="postventa-empty-state-link"
              >
                Asignar ejecutivo
              </Link>
              {'. Por eso “Mis clientes” puede aparecer en 0.'}
            </span>
          </div>
        )}
      </div>

      {/* =====================================================================
          PASO 2 — ¿A quién?  Scope chips (by ejecutivo) + manual checkboxes in
          the grid. Counts are live. (Roger 2026-09-04: renamed asesor->ejecutivo
          consistently; "Sin asesor" chips removed — asesor is B.PRO info only.)
          ===================================================================== */}
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
          2
        </span>
        <h2 className="text-sm font-semibold text-gray-900">¿A quién?</h2>
      </div>
      {/* Ejecutivo scope chips + count badges (who will I contact). */}
      <div
        className="mb-4 flex flex-wrap items-center gap-2"
        data-testid="postventa-asesor-chips"
      >
        <span className="text-sm font-medium text-gray-600">Ver:</span>
        {ASESOR_SCOPES.map((s) => {
          const active = asesorScope === s.key;
          const isMeScope = s.key === 'me' || s.key === 'me+none';
          // MIS-CLIENTES CHIP (oct23, td473 verify P1): the asesor-keyed "Mis
          // clientes" / "Mis + sin ejecutivo" chips only mean something when
          // the logged-in user HAS a B.PRO asesor mapping. Without one they
          // rendered first, disabled, saying "0" (dead for Grupo Cruces) while
          // the real "Mis clientes (ejecutivo)" chip sat further right. Hide
          // them entirely so the ejecutivo chip becomes THE "Mis clientes".
          if (isMeScope && !myAsesorName) return null;
          const count = asesorCounts[s.key] ?? 0;
          return (
            <button
              key={s.key}
              type="button"
              data-testid={`asesor-chip-${s.key}`}
              onClick={() => setAsesorScope(s.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                active
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {s.label}
              <span
                className={`rounded-full px-1.5 text-xs ${
                  active ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
        {/* Ejecutivo-de-citas filter chip. Three states (oct23):
            - asesor mapped (myAsesorName): the asesor chips above keep the
              "Mis clientes" label, so this one stays "Mis clientes (ejecutivo)"
              and is shown only when the user is an ejecutivo OR the catalog
              exists (unchanged behavior).
            - ejecutivo only: the asesor chips are hidden, so this chip IS the
              single "Mis clientes" (filters by ejecutivo_citas == my catalog
              row, matched by login email on the backend).
            - neither: always rendered as "Mis clientes", disabled, with the
              REAL admin path in the tooltip (Equipo → Ejecutivos de citas). */}
        {(!myAsesorName || ejecutivos.length > 0 || myEjecutivoName) && (
          <button
            type="button"
            data-testid="ejecutivo-chip-me"
            disabled={!myEjecutivoName}
            title={
              !myEjecutivoName ? MIS_CLIENTES_NOT_IN_CATALOG_TOOLTIP : undefined
            }
            onClick={() =>
              setEjecutivoScope((s) => (s === 'me' ? 'all' : 'me'))
            }
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
              ejecutivoScope === 'me'
                ? 'border-teal-600 bg-teal-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {myAsesorName ? 'Mis clientes (ejecutivo)' : 'Mis clientes'}
          </button>
        )}
      </div>

      {/* Search / filter controls (Roger 2026-09-04): moved here from under the
          KPI cards. Searching + filtering the cartera is part of CHOOSING who to
          contact, so the bar lives inside Paso 2 — below the scope chips, above
          the selection bar and the client table it narrows. */}
      <div
        className="flex flex-wrap items-center gap-3 mb-4"
        data-testid="postventa-filter-controls"
      >
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search', 'Buscar') + '…'}
          className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none"
        />
        <select
          value={dueFilter}
          onChange={(e) => setDueFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="all">Todos</option>
          <option value="due">Servicio vencido</option>
          <option value="upcoming">Próximos</option>
        </select>
        {/* Sucursal filter: narrows by the row's normalized sucursal (cartera
            `sucursal`, fallback lugar_servicio / lugar_compra). */}
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <span className="font-medium">Sucursal</span>
          <select
            data-testid="postventa-sucursal-filter"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="all">{t('common.all', 'Todas')}</option>
            {locationOptions.map((loc) => (
              <option key={loc} value={loc}>
                {loc === '__none__' ? 'Sin sucursal' : loc}
              </option>
            ))}
          </select>
        </label>
        {/* Test / QA rows: hidden by default, one click to show them (badge
            "Prueba" on each). Only rendered when the cartera has any. */}
        {testRowCount > 0 && (
          <label
            className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-600"
            data-testid="postventa-test-rows-toggle"
            title="Registros de prueba del equipo (nombre TEST/PRUEBA, VIN de prueba o etiqueta test_tag). No son clientes reales."
          >
            <input type="checkbox" checked={showTestRows} onChange={toggleShowTestRows} />
            {`Mostrar pruebas (${testRowCount})`}
          </label>
        )}
        {/* P0 PEN-primary (oct23): GM "meses sin visita" band (overlapping bands,
            membership test on the row's bandas_servicio stamped by the PEN ingest). */}
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <span className="font-medium">Meses sin visita</span>
          <select
            data-testid="postventa-visita-band-filter"
            value={visitaFilter}
            onChange={(e) => setVisitaFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="all">{t('common.all', 'Todos')}</option>
            {VISITA_BANDS.map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </select>
        </label>
        {/* Fuera de planta (> 96 meses / modelo <= año-8): hidden by default, one
            click to show them (badge on each). Never dialable. Only rendered when
            the cartera has any. */}
        {fueraPlantaCount > 0 && (
          <label
            className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-600"
            data-testid="postventa-fuera-planta-toggle"
            title="Unidades fuera de la ventana de planta (más de 96 meses / modelo anterior a la ventana). No se contactan; se conservan sólo como registro."
          >
            <input type="checkbox" checked={showFueraPlanta} onChange={(e) => setShowFueraPlanta(e.target.checked)} />
            {`Mostrar fuera de planta (${fueraPlantaCount})`}
          </label>
        )}
        <button
          onClick={load}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
        >
          {t('common.refresh', 'Actualizar')}
        </button>
      </div>

      {/* ticket v2: Seguimiento chips (7 filters with counts) + the count-only
          "Sin iniciar" counter. Counts come from the other filters' result,
          before this chip, overdue rows only. */}
      <div
        className="mb-4 flex flex-wrap items-center gap-2"
        data-testid="postventa-seguimiento-chips"
      >
        <span className="text-sm font-medium text-gray-600">
          {t('postventa.followup.column', 'Seguimiento')}:
        </span>
        {[{ key: 'all', label: t('common.all', 'Todos') }, ...SEGUIMIENTO_FILTERS].map((f) => {
          const active = seguimientoFilter === f.key;
          const count = f.key === 'all' ? seguimientoCounts.total : (seguimientoCounts[f.key] ?? 0);
          return (
            <button
              key={f.key}
              type="button"
              data-testid={`seguimiento-chip-${f.key}`}
              onClick={() => setSeguimientoFilter(f.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                active
                  ? 'border-teal-600 bg-teal-600 text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {f.key === 'all' ? f.label : t(`postventa.followup.filters.${f.key}`, f.label)}
              <span
                className={`rounded-full px-1.5 text-xs ${active ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                {count}
              </span>
            </button>
          );
        })}
        <span
          className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-gray-300 px-3 py-1 text-sm text-gray-500"
          data-testid="seguimiento-counter-sin_iniciar"
          title={t('postventa.followup.sinIniciarHint', 'Vencidos sin ningún intento todavía (no es un filtro)')}
        >
          {t('postventa.followup.states.sin_iniciar', 'Sin iniciar')}
          <span className="rounded-full bg-gray-100 px-1.5 text-xs text-gray-600">{seguimientoCounts.sin_iniciar}</span>
        </span>
      </div>

      {/* Manual multi-selection action bar (only when ≥1 row is hand-picked). */}
      {selectedVins.size > 0 && (
        <div
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2"
          data-testid="postventa-selection-bar"
        >
          <span className="text-sm font-medium text-indigo-800">
            {selectedVins.size} seleccionado(s)
          </span>
          {/* MASS-LAUNCH CAP (oct23): inline warning once the selection exceeds
              the per-launch cap. The backend refuses > cap for managers; an
              admin must acknowledge the count in Paso 3 to proceed. */}
          {selectionOverCap && (
            <span
              data-testid="postventa-selection-cap-warning"
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800"
            >
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
              {`${selectedVins.size} clientes seleccionados — más de ${POSTVENTA_OUTREACH_MAX_ITEMS} por contacto. Se pedirá confirmación explícita; solo un administrador puede lanzar más de ${POSTVENTA_OUTREACH_MAX_ITEMS}.`}
            </span>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {/* Bulk-assign of the selection now lives in the header
                "[Asignar ejecutivo]" panel (Roger 2026-09-04), so the selection
                bar keeps only its primary action ("Iniciar contacto") + Limpiar
                and stays unambiguous. */}
            <button
              type="button"
              onClick={() => setSelectedVins(new Set())}
              className="rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
            >
              Limpiar
            </button>
            {/* Paso 3 — ONE primary action. Disabled until an Agente IA is chosen
                in Paso 1 (Roger: the agent is REQUIRED, no auto-select). Opens
                the confirm dialog summarizing n · agente · canal · modo piloto
                BEFORE the outreach modal. The label carries the chosen agent so
                the user always sees who will contact. */}
            <button
              type="button"
              data-testid="postventa-start-selected"
              disabled={!agentId}
              title={!agentId ? 'Elige un Agente IA (Paso 1) para continuar' : undefined}
              onClick={() => {
                const vins = Array.from(selectedVins);
                if (!vins.length) return;
                if (!agentId) {
                  toast.error('Elige un Agente IA (Paso 1) para continuar');
                  return;
                }
                setMassAck(false);
                setPendingContact({
                  vins,
                  asesorName: contactarComo,
                  source: 'selection',
                });
              }}
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PhoneArrowUpRightIcon className="h-4 w-4" />
              Iniciar contacto ({selectedVins.size})
              {chosenAgent ? ` con ${chosenAgent.name}` : ''}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-md bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {COLUMNS.map((c) => {
                // The manual multi-select column: a select-all-visible checkbox
                // in the header, no sort.
                if (c.key === 'select') {
                  return (
                    <th key={c.key} className="px-3 py-2.5 text-left">
                      <input
                        type="checkbox"
                        data-testid="postventa-select-all"
                        aria-label="Seleccionar página"
                        title="Seleccionar página (solo las filas visibles)"
                        checked={allPageSelected}
                        onChange={toggleSelectPage}
                      />
                    </th>
                  );
                }
                return (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c.key)}
                    className="cursor-pointer select-none px-4 py-3 text-left font-medium text-gray-600 hover:text-gray-900"
                  >
                    {c.label}
                    {sortKey === c.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                );
              })}
              <th className="px-3 py-2.5 text-right font-medium text-gray-600" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="px-4 py-8 text-center text-gray-400">
                  {t('common.loading', 'Cargando...')}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length + 1}
                  className="px-4 py-8 text-center text-gray-500"
                  data-testid="postventa-empty-state"
                >
                  {(vehicles || []).length === 0
                    ? 'Aún no hay clientes en la cartera de postventa. Se cargan con la sincronización B.PRO o con una importación.'
                    : 'Ningún cliente coincide con la búsqueda o los filtros actuales. Prueba con otro nombre, teléfono, VIN o sucursal, o cambia los filtros.'}
                  {hiddenTestMatches > 0 ? (
                    <span className="mt-1 block text-xs text-amber-700" data-testid="postventa-empty-state-test-hint">
                      {hiddenTestMatches === 1
                        ? 'Hay 1 registro de prueba que coincide; activa “Mostrar pruebas” para verlo.'
                        : `Hay ${hiddenTestMatches} registros de prueba que coinciden; activa “Mostrar pruebas” para verlos.`}
                    </span>
                  ) : null}
                </td>
              </tr>
            ) : (
              pageInfo.items.map((v) => {
                const last = lastContact(v);
                const attempts = Array.isArray(v.contact_history)
                  ? v.contact_history.length
                  : 0;
                return (
                <tr
                  key={v.vin}
                  onClick={() => setSelected(v)}
                  className="cursor-pointer hover:bg-teal-50"
                >
                  {COLUMNS.map((c) => {
                    // Manual multi-select checkbox (stopPropagation so it does
                    // not open the drawer).
                    if (c.key === 'select') {
                      return (
                        <td
                          key={c.key}
                          className="px-3 py-2.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            data-testid={`postventa-select-${v.vin}`}
                            aria-label={`Seleccionar ${v.vin}`}
                            checked={selectedVins.has(v.vin)}
                            onChange={() => toggleSelectVin(v.vin)}
                          />
                        </td>
                      );
                    }
                    if (c.key === 'nombre') {
                      const nombre = String(v.nombre || '').trim();
                      const tel = String(v.telefono || '').trim();
                      return (
                        <td key={c.key} className="px-3 py-2.5" data-testid={`postventa-cliente-${v.vin}`}>
                          <div className="flex flex-col">
                            <span className={`flex flex-wrap items-center gap-1.5 ${nombre ? 'font-medium text-gray-900' : 'text-gray-400'}`}>
                              {nombre || 'Sin nombre'}
                              {isTestRow(v) && (
                                <span
                                  className="inline-block rounded-full border border-dashed border-amber-400 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
                                  data-testid={`postventa-test-badge-${v.vin}`}
                                  title="Registro de prueba del equipo, no es un cliente real."
                                >
                                  Prueba
                                </span>
                              )}
                              {isFueraDePlanta(v) && (
                                <span
                                  className="inline-block rounded-full border border-gray-400 bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600"
                                  data-testid={`postventa-fuera-planta-badge-${v.vin}`}
                                  title={`Fuera de planta — no se contacta. ${v.fuera_de_planta_reason || ''}`.trim()}
                                >
                                  Fuera de planta
                                </span>
                              )}
                            </span>
                            <span className={`text-xs ${tel ? 'text-gray-600' : 'text-amber-600'}`}>
                              {tel || 'Sin teléfono'}
                            </span>
                          </div>
                        </td>
                      );
                    }
                    if (c.key === 'modelo') {
                      const u = unidadParts(v);
                      const hasUnidad = !!(u.marca || u.modelo || u.version || u.anio || u.transmision);
                      const vin = String(v.vin || '').trim();
                      return (
                        <td key={c.key} className="px-3 py-2.5" data-testid={`postventa-unidad-${v.vin}`}>
                          <div className="flex flex-col">
                            {hasUnidad ? (
                              <UnidadInline parts={u} />
                            ) : (
                              <span className="text-gray-400">Unidad sin datos</span>
                            )}
                            {vin ? (
                              <span className="font-mono text-[11px] text-gray-500" title="Serie (VIN)">
                                {vin}
                              </span>
                            ) : null}
                          </div>
                        </td>
                      );
                    }
                    if (c.key === 'sucursal') {
                      const label = rowSucursal(v);
                      const raw = String(v.sucursal || v.lugar_servicio || v.lugar_compra || '').replace(/\u00a0/g, ' ').trim();
                      return (
                        <td
                          key={c.key}
                          className={`px-3 py-2.5 ${label ? 'text-gray-800' : 'text-gray-300'}`}
                          data-testid={`postventa-sucursal-${v.vin}`}
                          title={raw && raw !== label ? `En la cartera: ${raw}` : undefined}
                        >
                          {label || ''}
                        </td>
                      );
                    }
                    if (c.key === 'ultimo_servicio') {
                      const fecha = esDateOrNull(v.ultimo_servicio) || (v.ultimo_servicio ? String(v.ultimo_servicio) : '');
                      const km = formatKm(v.km_ultimo_servicio);
                      return (
                        <td key={c.key} className="px-3 py-2.5" data-testid={`postventa-ultimo-servicio-${v.vin}`}>
                          <div className="flex flex-col">
                            <span className={fecha ? 'text-gray-800' : 'text-gray-300'}>{fecha || '—'}</span>
                            {km ? <span className="text-xs text-gray-500">{km}</span> : null}
                          </div>
                        </td>
                      );
                    }
                    if (c.key === 'overdue') {
                      const due = esDateOrNull(v.next_service_due);
                      const d = daysOverdue(v);
                      return (
                        <td key={c.key} className="px-3 py-2.5" data-testid={`postventa-proximo-servicio-${v.vin}`}>
                          <div className="flex flex-col gap-1">
                            {/* The date itself for overdue rows (the badge already
                                names it for upcoming ones). */}
                            {due && d != null && d >= 0 ? (
                              <span className="text-gray-800">{due}</span>
                            ) : null}
                            <OverdueBadge vehicle={v} />
                          </div>
                        </td>
                      );
                    }
                    if (c.key === 'seguimiento') {
                      // Dedicated branch: the generic fallback below renders
                      // String(val) and would show "[object Object]" for a map.
                      return (
                        <td key={c.key} className="px-3 py-2.5">
                          <SeguimientoCell t={t} vehicle={v} />
                        </td>
                      );
                    }
                    if (c.key === 'asesor') {
                      const name = asesorOf(v);
                      return (
                        <td
                          key={c.key}
                          className={`px-3 py-2.5 ${name ? 'text-gray-800' : 'text-gray-300'}`}
                        >
                          {name || 'Sin asesor'}
                        </td>
                      );
                    }
                    if (c.key === 'ejecutivo') {
                      // Single-row assign menu: pick an ejecutivo de citas (or
                      // "Sin ejecutivo" to clear). stopPropagation so the select
                      // does not open the drawer.
                      const cur = ejecutivoOf(v);
                      return (
                        <td
                          key={c.key}
                          className="px-3 py-2.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <select
                            data-testid={`postventa-ejecutivo-${v.vin}`}
                            value={cur}
                            disabled={assigningEjecutivo}
                            onChange={(e) => assignEjecutivo([v.vin], e.target.value)}
                            className={`border border-gray-200 rounded-md py-1 px-2 text-xs bg-white ${
                              cur ? 'text-gray-800' : 'text-gray-400'
                            }`}
                          >
                            <option value="">Sin ejecutivo</option>
                            {ejecutivos.map((ej) => (
                              <option key={ej.nombre} value={ej.nombre}>
                                {ej.nombre}
                              </option>
                            ))}
                            {/* Preserve an assigned-but-uncatalogued name so it stays visible. */}
                            {cur &&
                              !ejecutivos.some(
                                (ej) => ej.nombre.toLowerCase() === cur.toLowerCase()
                              ) && <option value={cur}>{cur}</option>}
                          </select>
                        </td>
                      );
                    }
                    if (c.key === 'ultimo_contacto') {
                      return (
                        <td key={c.key} className="px-3 py-2.5">
                          <LastContactCell t={t} vehicle={v} last={last} />
                          {attempts > 0 ? (
                            <span className="mt-1 block text-xs text-gray-500" data-testid={`postventa-intentos-${v.vin}`}>
                              {attempts === 1 ? '1 intento' : `${attempts} intentos`}
                            </span>
                          ) : null}
                        </td>
                      );
                    }
                    let val;
                    if (c.key === 'intentos') {
                      val = attempts;
                    } else {
                      val = v[c.key];
                    }
                    const empty =
                      val == null ||
                      val === '' ||
                      (c.key === 'intentos' && !attempts);
                    // Location columns render a truly BLANK cell when absent
                    // (a car may be bought and serviced in different places;
                    // not-yet-serviced => lugar_servicio empty => no placeholder).
                    const blankNoDash =
                      c.key === 'lugar_compra' || c.key === 'lugar_servicio';
                    return (
                      <td
                        key={c.key}
                        className={`px-3 py-2.5 ${empty ? 'text-gray-300' : 'text-gray-800'}`}
                      >
                        {empty ? (blankNoDash ? '' : '—') : String(val)}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Default "Contactar como" to THIS row's asesor (stamp
                        // only). Agent stays manual + required (no auto-select).
                        const rowAsesor = asesorOf(v) || contactarComo;
                        setAgentId('');
                        setCallerIds([]);
                        setSingleAttempt(false);
                        setOutreach({ vins: [v.vin], asesorName: rowAsesor });
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-indigo-200 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
                    >
                      <PhoneArrowUpRightIcon className="h-3.5 w-3.5" />
                      Llamar
                    </button>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
        {!loading && rows.length > 0 && (
          <div
            className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-600"
            data-testid="postventa-pager"
          >
            <span data-testid="postventa-pager-range">
              Mostrando {pageInfo.from}–{pageInfo.to} de {pageInfo.total}
            </span>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1">
                <span>Por página</span>
                <select
                  data-testid="postventa-page-size"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value) || POSTVENTA_PAGE_SIZE_DEFAULT)}
                  className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                >
                  {POSTVENTA_PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                data-testid="postventa-page-prev"
                disabled={pageInfo.page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="rounded-md border border-gray-200 bg-white px-2 py-1 disabled:opacity-40"
              >
                ◀ Anterior
              </button>
              <span data-testid="postventa-page-indicator">
                {pageInfo.page + 1} / {pageInfo.pageCount}
              </span>
              <button
                type="button"
                data-testid="postventa-page-next"
                disabled={pageInfo.page >= pageInfo.pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageInfo.pageCount - 1, p + 1))}
                className="rounded-md border border-gray-200 bg-white px-2 py-1 disabled:opacity-40"
              >
                Siguiente ▶
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        // Backdrop is a light, mostly-transparent scrim (not a full black
        // dimmer) so the postventa list stays visible/legible behind the
        // drawer. Roger #1209 fu: "why is it disappearing the background".
        // Clicking the scrim still closes the drawer; the panel keeps its
        // shadow for depth.
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/5"
            onClick={() => setSelected(null)}
          />
          <div className="relative z-10 h-full w-full max-w-3xl overflow-y-auto bg-white shadow-2xl ring-1 ring-black/10 lg:max-w-4xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="min-w-0">
                {/* Title = the client (what the asesor is looking for) + the unit
                    on the same line; the sucursal reads right under it. A row
                    with no name falls back to the unit, then the VIN. */}
                <h2 className="text-lg font-semibold text-gray-900" data-testid="postventa-detail-title">
                  {String(selected.nombre || '').trim() ? (
                    <>
                      {String(selected.nombre).trim()}
                      {unidadLabel(selected) ? (
                        <span className="font-normal text-gray-600">
                          {' · '}
                          <UnidadInline parts={unidadParts(selected)} className="text-gray-700" />
                        </span>
                      ) : null}
                    </>
                  ) : unidadLabel(selected) ? (
                    <UnidadInline parts={unidadParts(selected)} className="text-gray-900" />
                  ) : (
                    selected.vin
                  )}
                </h2>
                {rowSucursal(selected) ? (
                  <p className="text-sm text-gray-600" data-testid="postventa-detail-subtitle">
                    {rowSucursal(selected)}
                  </p>
                ) : null}
                {/* Full B.PRO unit description (libro-de-ventas enrichment)
                    as a subtitle when it adds info beyond the short modelo. */}
                {selected.descripcion_unidad &&
                  selected.descripcion_unidad !== selected.modelo && (
                    <p
                      className="truncate text-xs text-gray-500"
                      data-testid="postventa-detail-descripcion-unidad"
                    >
                      {selected.descripcion_unidad}
                    </p>
                  )}
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-700">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-4 px-6 py-5 text-sm">
              {isTestRow(selected) && (
                <div
                  className="rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                  data-testid="postventa-detail-test-banner"
                >
                  Registro de prueba del equipo — no es un cliente real.
                </div>
              )}
              {/* Detail fields grouped the way an asesor reads a ficha: Cliente ·
                  Unidad · Servicio · Cita · Contacto. Two-column grid inside each
                  group on wider drawers. Internal knobs (cadence months, raw
                  cartera labels, enum keys) are folded into human lines. */}
              {drawerSections(selected, t).map((section) => (
                <div key={section.title} data-testid={`postventa-detail-section-${section.key}`}>
                  <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {section.title}
                  </h3>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                    {section.rows.map(([label, val, opts]) => (
                      <div
                        key={label}
                        className="flex justify-between gap-4 border-b border-gray-50 pb-2"
                        title={opts && opts.title ? opts.title : undefined}
                      >
                        <span className="text-gray-500">{label}</span>
                        <span
                          className={`text-right ${val != null && val !== '' ? 'font-medium text-gray-900' : 'text-gray-300'} ${opts && opts.mono ? 'font-mono text-xs' : ''}`}
                        >
                          {val != null && val !== '' ? String(val) : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* oct20 CADENCE CAP (Sergio): contact-cadence state of this
                  vehicle + "Reanudar contacto" when the automation stopped
                  after N cycles without contact (logged who/when). */}
              {(() => {
                const fu = followupStatusLine(selected);
                if (!fu) return null;
                const map = selected.followup || {};
                return (
                  <div
                    className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
                    data-testid="postventa-followup-panel"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col">
                        <span className="text-xs uppercase tracking-wide text-gray-400">Cadencia de contacto</span>
                        <span
                          className={`mt-1 inline-block w-fit rounded-full px-2 py-0.5 text-xs font-medium ${FOLLOWUP_TONE[fu.tone] || FOLLOWUP_TONE.neutral}`}
                          data-testid="postventa-followup-label"
                        >
                          {fu.text}
                        </span>
                        {map.relaunched_at ? (
                          <span className="mt-1 text-xs text-gray-500" data-testid="postventa-followup-relaunched">
                            Reanudado por {map.relaunched_by_email || map.relaunched_by || '—'}
                            {formatDateEs(map.relaunched_at) ? ` · ${formatDateEs(map.relaunched_at)}` : ''}
                            {Number(map.relaunch_count) > 1 ? ` · ${map.relaunch_count} veces` : ''}
                          </span>
                        ) : null}
                        {fu.status === 'stopped' ? (
                          <span className="mt-1 text-xs text-gray-500">
                            La automatización se detuvo para este servicio vencido. Se reanuda sola con un nuevo
                            servicio vencido, o ahora mismo con el botón.
                          </span>
                        ) : null}
                      </div>
                      {fu.canRelaunch ? (
                        <button
                          type="button"
                          data-testid="postventa-followup-relaunch"
                          disabled={relaunchingVin === selected.vin}
                          onClick={() => relaunchFollowup(selected)}
                          className="inline-flex items-center gap-1 rounded-md border border-teal-600 bg-white px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-50 disabled:opacity-50"
                        >
                          <PhoneArrowUpRightIcon className="h-4 w-4" />
                          {relaunchingVin === selected.vin ? 'Reanudando…' : 'Reanudar contacto'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })()}

              {/* ticket v2: derived "Estado de seguimiento" + the attempts of
                  the CURRENT due-service context (read-only). Sits next to the
                  cadence panel above; both read different maps. */}
              {(() => {
                const line = seguimientoLine(selected, t);
                if (!line) return null;
                const map = line.map || {};
                const attempts = seguimientoAttempts(selected);
                const fmtIso = (iso) => (iso ? formatDateEs(iso) : '');
                return (
                  <div
                    className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
                    data-testid="postventa-seguimiento-panel"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="text-xs uppercase tracking-wide text-gray-400">
                        {t('postventa.followup.column', 'Seguimiento')}
                      </span>
                      <span
                        className={`inline-block w-fit rounded-full px-2 py-0.5 text-xs font-medium ${FOLLOWUP_TONE[line.tone] || FOLLOWUP_TONE.neutral}`}
                        data-testid="postventa-seguimiento-label"
                      >
                        {line.text}
                      </span>
                      {line.chips.length ? (
                        <span className="flex flex-wrap gap-1">
                          {line.chips.map((c) => (
                            <span key={c} className="inline-block rounded-full border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-600">
                              {c}
                            </span>
                          ))}
                        </span>
                      ) : null}
                      <span className="text-xs text-gray-500">
                        {t('postventa.followup.since', 'Desde')}: {fmtIso(map.since) || '—'}
                        {map.next_action_at ? ` · ${t('postventa.followup.nextAction', 'Próxima acción')}: ${fmtIso(map.next_action_at)}` : ''}
                        {map.service_due ? ` · ${t('postventa.followup.serviceDue', 'Servicio')}: ${esDateOrNull(map.service_due) || map.service_due}` : ''}
                      </span>
                      {map.backfill_ambiguous || map.ambiguous ? (
                        <span className="text-xs text-amber-700" data-testid="postventa-seguimiento-ambiguous">
                          {t('postventa.followup.ambiguous', 'Historial legado ambiguo: no se contaron ciclos no verificables')}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-2">
                      <span className="text-xs font-medium text-gray-600">
                        {t('postventa.followup.attempts', 'Intentos de seguimiento')} ({attempts.length})
                      </span>
                      {attempts.length ? (
                        <ul className="mt-1 space-y-1" data-testid="postventa-seguimiento-attempts">
                          {attempts.map((a, i) => (
                            <li key={`${a.call_sid || i}-${i}`} className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
                              <span>{formatDateEs(a.ts) || '—'}</span>
                              {a.delivery_status ? <span className="text-gray-500">{a.delivery_status}</span> : null}
                              {a.outcome ? <span className="text-gray-500">{outcomeLabel(t, outcomeKey(a.outcome)) || a.outcome}</span> : null}
                              {a.error_code ? <span className="text-red-600">error {a.error_code}</span> : null}
                              {a.phone_masked ? <span className="text-gray-400">{a.phone_masked}</span> : null}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="mt-1 text-xs text-gray-400">
                          {t('postventa.followup.noAttempts', 'Sin intentos en este servicio')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* 3 contact profiles (propietario / mantenimiento / usuario) +
                  fallback order. Expander with an "Editar perfiles" button that
                  opens the edit modal. Also shows the last profile that answered
                  (contactado vía) at the top when known. */}
              <ContactProfilesSection
                vehicle={selected}
                onEdit={() => setEditProfiles(selected)}
              />

              {Array.isArray(selected.contact_history) &&
                selected.contact_history.length > 0 && (
                  <div className="pt-2">
                    <h3 className="mb-2 font-medium text-gray-700">
                      {t('postventa.contactHistory', 'Historial de contacto')}
                    </h3>
                    <NewestFirstList
                      items={selected.contact_history.slice().reverse()}
                      moreLabel="Ver historial anterior"
                      lessLabel="Ver menos"
                      renderItem={(ev, i) => (
                        <li key={i} className="rounded-md bg-gray-50 px-3 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-gray-800">
                              {outcomeLabel(t, outcomeKey(ev.outcome)) || ev.source || 'evento'}
                            </span>
                            {ev.ts != null && ev.ts !== '' && (
                              <span className="text-xs text-gray-400">
                                {formatDateEs(ev.ts) || '—'}
                              </span>
                            )}
                          </div>
                          {ev.channel && (
                            <div className="text-xs tracking-wide text-gray-400">
                              {channelLabelEs(ev.channel)}
                            </div>
                          )}
                          {contactedViaLabel(ev) && (
                            <div
                              data-testid="postventa-contacted-via"
                              className="text-xs font-medium text-indigo-600"
                            >
                              {`Contactado vía: ${contactedViaLabel(ev)}`}
                            </div>
                          )}
                          {(ev.note || ev.summary) && (
                            <div className="whitespace-pre-wrap text-gray-500">{plainSummary(ev.note || ev.summary)}</div>
                          )}
                        </li>
                      )}
                    />
                  </div>
                )}

              <InteraccionesSection vehicle={selected} />
            </div>
          </div>
        </div>
      )}

      {/* Edit contact profiles modal (add/fix up to 3 profiles by hand). */}
      {editProfiles && (
        <EditProfilesModal
          vehicle={editProfiles}
          onClose={() => setEditProfiles(null)}
          onSaved={(contacts) => {
            // Optimistically reflect the new profiles in the open drawer + list
            // so the change shows without a full reload.
            setSelected((s) => (s && s.vin === editProfiles.vin ? { ...s, contacts } : s));
            setVehicles((vs) =>
              vs.map((v) => (v.vin === editProfiles.vin ? { ...v, contacts } : v))
            );
          }}
        />
      )}

      {/* --- PASO 3 confirm dialog (Roger 2026-09-04) ---------------------------
          Summarizes the contact BEFORE the outreach modal: n clientes · agente
          (+ department) · canal (llamada primero → WhatsApp) · modo piloto note.
          "Confirmar" hands off to the outreach modal (caller-number pick + real
          launch). The chosen agent is carried from Paso 1 — the user has already
          seen exactly who will call/write. */}
      {pendingContact && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="postventa-contact-confirm"
        >
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="flex items-center gap-2 border-b border-gray-200 px-5 py-4">
              <PhoneArrowUpRightIcon className="h-5 w-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Iniciar contacto ({pendingContact.vins.length})
                {chosenAgent ? ` con ${chosenAgent.name}` : ''}
              </h2>
            </div>
            <div className="px-5 py-4 text-sm">
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">Clientes</span>
                  <span className="font-semibold text-gray-900">
                    {pendingContact.vins.length}
                  </span>
                </li>
                <li className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">Agente</span>
                  <span className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">
                      {chosenAgent ? chosenAgent.name : '—'}
                    </span>
                    {chosenAgent && (
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${departmentBadgeClass(
                          chosenAgent.department
                        )}`}
                      >
                        {departmentLabel(chosenAgent.department)}
                      </span>
                    )}
                  </span>
                </li>
                <li className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">Canal</span>
                  <span className="font-medium text-gray-900">
                    Llamada primero → WhatsApp
                  </span>
                </li>
              </ul>
              {pilotMode && (
                <div
                  data-testid="postventa-confirm-pilot-note"
                  className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                >
                  Modo piloto: solo números autorizados (algunos de los
                  {' '}{pendingContact.vins.length} se marcarán; el resto vuelve
                  como omitido por modo piloto).
                </div>
              )}
              {/* MASS-LAUNCH CAP (oct23): over the cap the user must tick an
                  explicit acknowledgement that STATES THE COUNT before
                  "Confirmar" enables. Managers are told the backend will refuse
                  it; the page sends allow_mass only after this acknowledgement
                  and the backend still enforces admin-only. */}
              {pendingContact.vins.length > POSTVENTA_OUTREACH_MAX_ITEMS && (
                <div
                  data-testid="postventa-confirm-mass-warning"
                  className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
                >
                  <div className="flex items-start gap-2">
                    <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>
                      {`Vas a contactar a ${pendingContact.vins.length} clientes, más del máximo de ${POSTVENTA_OUTREACH_MAX_ITEMS} por contacto. Solo un administrador puede lanzar un contacto masivo; de lo contrario el sistema lo rechazará.`}
                    </span>
                  </div>
                  <label className="mt-2 flex cursor-pointer items-start gap-2 font-medium">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      data-testid="postventa-confirm-mass-ack"
                      checked={massAck}
                      onChange={(e) => setMassAck(e.target.checked)}
                    />
                    <span>
                      {`Entiendo que se contactará a ${pendingContact.vins.length} clientes.`}
                    </span>
                  </label>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button
                type="button"
                onClick={() => setPendingContact(null)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                data-testid="postventa-contact-confirm-btn"
                disabled={
                  pendingContact.vins.length > POSTVENTA_OUTREACH_MAX_ITEMS && !massAck
                }
                title={
                  pendingContact.vins.length > POSTVENTA_OUTREACH_MAX_ITEMS && !massAck
                    ? `Confirma que entiendes que se contactará a ${pendingContact.vins.length} clientes`
                    : undefined
                }
                onClick={() => {
                  const { vins, asesorName } = pendingContact;
                  const overCap = vins.length > POSTVENTA_OUTREACH_MAX_ITEMS;
                  if (overCap && !massAck) return;
                  setPendingContact(null);
                  // Carry the Paso 1 agent into the outreach modal; only the
                  // caller number(s) remain to pick there.
                  setCallerIds([]);
                  setSingleAttempt(false);
                  setOutreach({ vins, asesorName, allowMass: overCap && massAck });
                }}
                className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <PhoneArrowUpRightIcon className="h-4 w-4" />
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Outreach modal: pick agent + caller number(s), launch. */}
      {outreach && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !launching && setOutreach(null)}
          />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Iniciar contacto ({outreach.vins.length})
              </h2>
              <button
                onClick={() => !launching && setOutreach(null)}
                className="text-gray-400 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4 text-sm">
              {/* POSTVENTA-PILOT-CALL-GATE (sep3): when pilot mode is ON, only
                  whitelisted numbers are dialed — the same guard WhatsApp uses.
                  Non-whitelisted selections come back as "omitido por modo
                  piloto" instead of dialing. */}
              {pilotMode && (
                <div
                  data-testid="postventa-outreach-pilot-note"
                  className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800"
                >
                  Modo piloto: solo se marca a números autorizados.
                </div>
              )}
              {/* Contactar como: stamped on every contact for the cita/CRM/B.PRO
                  write-back. Does NOT auto-select an agent (Roger addendum
                  2026-09-03) — the agent below is chosen manually. */}
              {(asesores.length > 0 || myAsesorName) && (
                <div>
                  <label className="mb-1 block font-medium text-gray-700">
                    Ejecutivo de citas (quién da seguimiento)
                  </label>
                  <select
                    data-testid="postventa-modal-contactar-como"
                    value={outreach.asesorName ?? contactarComo}
                    onChange={(e) => {
                      const name = e.target.value;
                      setOutreach((o) => ({ ...o, asesorName: name }));
                    }}
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  >
                    <option value="">Elegir ejecutivo…</option>
                    {asesores.map((a) => (
                      <option key={a.asesor_name} value={a.asesor_name}>
                        {a.asesor_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {/* Agente — pre-filled from Paso 1 (still editable here as a safety
                  net). Shows the department inline so the choice stays explicit
                  all the way through the flow. */}
              <div>
                <label className="mb-1 block font-medium text-gray-700">Agente</label>
                <select
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  <option value="">Elegir agente IA…</option>
                  {groupedAgents.postventa.length > 0 && (
                    <optgroup label="Postventa">
                      {groupedAgents.postventa.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name} — {departmentLabel(a.department)}
                          {a.language ? ` (${a.language})` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {groupedAgents.otros.length > 0 && (
                    <optgroup label="Otros">
                      {groupedAgents.otros.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name} — {departmentLabel(a.department)}
                          {a.language ? ` (${a.language})` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {chosenAgent && (
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                    <span>Agente:</span>
                    <span className="font-semibold text-gray-800">{chosenAgent.name}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 font-medium ${departmentBadgeClass(
                        chosenAgent.department
                      )}`}
                    >
                      {departmentLabel(chosenAgent.department)}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <label className="mb-1 block font-medium text-gray-700">
                  Número(s) saliente(s)
                </label>
                {numbers.length === 0 ? (
                  <div className="text-gray-400">No hay números disponibles</div>
                ) : (
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2">
                    {numbers.map((n) => (
                      <label
                        key={n.number}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={callerIds.includes(n.number)}
                          onChange={() => toggleCaller(n.number)}
                        />
                        <span className="text-gray-800">{n.number}</span>
                        {n.label && (
                          <span className="text-gray-400">({n.label})</span>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {/* SINGLE-ATTEMPT OPT-IN (sep6): default off => existing 3-attempt
                  behavior; when checked the backend caps the campaign at ONE
                  lifetime dial (max_retries=1), no auto-retry on
                  no-answer/busy/failed/canceled. */}
              <div>
                <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={singleAttempt}
                    onChange={(e) => setSingleAttempt(e.target.checked)}
                  />
                  <span>
                    <span className="font-medium">Un solo intento (sin reintentos)</span>
                    <span className="block text-xs text-gray-500">
                      Marca una sola vez a cada vehículo; no se reintenta si no
                      contesta, está ocupado o falla.
                    </span>
                  </span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button
                onClick={() => !launching && setOutreach(null)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={launchOutreach}
                disabled={launching}
                className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <PhoneArrowUpRightIcon className="h-4 w-4" />
                {launching ? 'Iniciando…' : 'Iniciar contacto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NOTE: the mass "Enviar recordatorios" confirm modal + its trigger were
          REMOVED from this working page (Roger 2026-09-04): postventa contact
          happens ONLY via Paso 1-2-3 with the pilot gate; this page never mass-
          sends. The backend send-due endpoint and its API client
          (sendDuePostventaReminders) are intentionally left intact for a future
          admin-only surface (Configuración → Postventa). */}
    </div>
  );
}

export default PostventaPage;
